import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, apiErr } from "@/foundation/api/client";
import { filterActiveInMonth, statusLabel } from "@/lib/employeeScope";
import { useAuth } from "@/foundation/auth/AuthContext";
import { useCompanySelection } from "@/foundation/context/ActiveCompanyContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useIsCompact } from "@/foundation/ui/hooks";
import { CompactMobileHeader } from "@/components/design-system";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MONTHS_IT, WEEKDAYS_SHORT, fmtHoursSigned, fmtHours } from "@/foundation/formatting";
import { CAUSE_CODES, CAUSE_SHORT, CAUSE_LABELS, CAUSE_COLORS, ATTENDANCE_CORE_CAUSES, ATTENDANCE_OPTIONAL_CAUSES, hydrateCauseCatalog } from "@/lib/constants";
import {
  Save, CheckCircle2, Send, XCircle, ClipboardList, Lock, Unlock, Sparkles,
  ArrowLeftRight, Wand2, Rows3, TableProperties, Plus, X, Info, Calendar, CalendarCheck2, Wrench,
  UserMinus, ChevronLeft, ChevronRight, EllipsisVertical,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { Clock3 } from "lucide-react";
import { SuiteCard, SuiteButton, SuiteEyebrow, SuiteBadge, SuiteMonthPicker } from "@/components/suite";
// FND · Enterprise Workspace (Sprint V3.1) — componenti Foundation riutilizzabili.
// Rif. /app/audit/SPRINT_V3_0B_ENTERPRISE_WORKSPACE.md
import { WorkspaceHeader } from "@/foundation/ui/workspace/WorkspaceHeader";
import { WorkspaceSidePanel, AIAssistantPlaceholder } from "@/foundation/ui/workspace/WorkspaceSidePanel";
import { sortCompaniesIt } from "@/lib/sortCompanies";
import { reconcileEmployeeWithCompany } from "@/lib/companyEmployeeCoherence";
import { announceShellCompany, companyNameForShell } from "@/foundation/context/shellCompany";
import { parseDuration, formatDuration, sanitizeDurationTyping, roundDurationStorage, durationToHours, useDurationFormat, getActiveDurationFormat, DurationFormatBadge, DurationLabel } from "@/foundation/duration";
import { formatDateIT } from "@/foundation/formatting";
import { useDirtyRegistrar } from "@/foundation/context";
import MonthWorkflowControl, { formatClosureAnomalies, isEditableState } from "@/components/MonthWorkflowControl";
import { useEffectiveRole } from "@/foundation/context";
import { ActionCatalog } from "@/foundation/actions";  // v1.5.4 Foundation - riutilizzabile da altri moduli (non piu' esposto nel Cartellino)
import { buildAttendanceActions } from "./attendance/AttendanceActions";
import {
  parseAttendanceCauseCellTestId,
  parseAttendanceHours,
  preserveManualHoursForSave,
  rebalanceOrdinaryHours,
  shouldPersistAttendanceRow,
  THEORETICAL_REDUCTION_CAUSES,
} from "@/lib/attendanceHours";

/** StatusBadge compatto — usa la palette Suite con label brevissime (Boz./Inv./Appr./Resp./Manc./Rip.)
 *  per non sforare mai la colonna Stato della griglia. Tooltip via title. */
function StatusBadge({ s }) {
  const map = {
    draft:     { tone: "neutral", label: "Boz.",  title: "Bozza" },
    sent:      { tone: "info",    label: "Inv.",  title: "Inviato" },
    approved:  { tone: "ok",      label: "Appr.", title: "Approvato" },
    rejected:  { tone: "err",     label: "Resp.", title: "Respinto" },
    missing:   { tone: "warn",    label: "Manc.", title: "Mancante" },
    rest:      { tone: "neutral", label: "Rip.",  title: "Riposo" },
  };
  const m = map[s] || map.draft;
  return <SuiteBadge tone={m.tone} size="sm" className="!text-[10px] xl:!text-[11px]" title={m.title}>{m.label}</SuiteBadge>;
}

/** CauseChipsEditor — vista "Chip": chip inline { causale, ore, ✕ } + bottone "+ Aggiungi". */
const DUR_INVALID = "!border-suite-error !ring-1 !ring-suite-error/40 !bg-red-50";
const isDurationInvalid = (v, fmt = getActiveDurationFormat()) =>
  v !== undefined && v !== null && v !== "" && !!parseDuration(v, fmt).error;

function CauseChipsEditor({ date, causeHours, onSet, onCommit, onEnter }) {
  const [adding, setAdding] = React.useState(false);
  const active = Object.keys(causeHours || {});
  const available = CAUSE_CODES.filter((c) => !active.includes(c));

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`att-chips-${date}`}>
      {active.map((c) => (
        <div key={c}
             className="inline-flex items-center gap-1 border border-suite-neutral-200 bg-suite-neutral-50 rounded-md pl-2 pr-1 py-0.5 text-xs"
             data-testid={`att-chip-${date}-${c}`}>
          <span className="font-semibold text-suite-neutral-700 whitespace-nowrap">{CAUSE_SHORT[c]}</span>
          <Input
            type="text"
            inputMode="decimal"
            pattern={getActiveDurationFormat() === "hm" ? undefined : "[0-9]*[.,]?[0-9]{0,2}"}
            value={String(causeHours[c] ?? "").replace(".", ",")}
            onChange={(e) => onSet(date, c, e.target.value)}
            onBlur={() => onCommit && onCommit(date, c)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (onCommit) onCommit(date, c);
                if (onEnter) onEnter(date, c);
              }
            }}
            aria-invalid={isDurationInvalid(causeHours[c]) || undefined}
            className={`no-spinner w-[52px] h-6 text-right font-mono !text-xs px-1 border-0 focus-visible:ring-1 ${isDurationInvalid(causeHours[c]) ? DUR_INVALID : ""}`}
            placeholder="0"
            data-testid={`att-chip-input-${date}-${c}`}
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => onSet(date, c, "")}
            className="text-suite-neutral-400 hover:text-suite-error transition-colors p-0.5"
            title={`Rimuovi ${CAUSE_LABELS[c]}`}
            data-testid={`att-chip-remove-${date}-${c}`}
          >
            <X size={11} strokeWidth={2.5} />
          </button>
        </div>
      ))}
      {available.length > 0 && (
        adding ? (
          <Select
            open
            value=""
            onValueChange={(cause) => { onSet(date, cause, "1,00"); setAdding(false); }}
            onOpenChange={(o) => { if (!o) setAdding(false); }}
          >
            <SelectTrigger className="h-7 w-8 p-0 border-suite-primary/40 text-suite-primary" data-testid={`att-add-trigger-${date}`}>
              <Plus size={13} className="mx-auto" />
            </SelectTrigger>
            <SelectContent>
              {available.map((c) => (
                <SelectItem key={c} value={c} data-testid={`att-add-opt-${date}-${c}`}>
                  <span className="font-semibold">{CAUSE_SHORT[c]}</span>
                  <span className="text-suite-neutral-500 ml-2 text-xs">{CAUSE_LABELS[c]}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-dashed border-suite-primary/40 text-suite-primary text-xs font-semibold hover:bg-suite-primary-light transition-colors"
            data-testid={`att-add-btn-${date}`}
          >
            <Plus size={11} /> Aggiungi
          </button>
        )
      )}
      {active.length === 0 && !adding && (
        <span className="text-[10px] text-suite-neutral-400 italic">Nessuna causale</span>
      )}
    </div>
  );
}

/** InlineKpi — KPI compatto inline nell'info-strip (nessuna cornice, solo label + valore mono). */
function InlineKpi({ label, value, tone, testid }) {
  const toneClass = tone === "emerald" ? "text-suite-accent-hover"
    : tone === "red" ? "text-suite-error"
    : tone === "sky" ? "text-suite-secondary"
    : "text-suite-neutral-800";
  return (
    <div className="flex items-baseline gap-1.5 whitespace-nowrap" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-widest text-suite-neutral-500 font-semibold">{label}</span>
      <span className={`text-sm font-mono font-bold ${toneClass}`}>{value}</span>
    </div>
  );
}

const HINT_CENTESIMI = "Regola: le ore vanno inserite in centesimi (100mi), NON in sessantesimi. Esempio: 8h e 30 minuti → 8,50 (non 8,30). Formula: minuti ÷ 60 = decimale.";

function causeRangeDayMode(cause) {
  if (cause?.behavior?.range_day_mode) return cause.behavior.range_day_mode;
  if (cause?.code === "CONGEDO_MATRIMONIALE") return "calendar_days";
  if (["MALATTIA", "MATERNITA", "CONG_PARENTALE"].includes(cause?.code)) return "exclude_sundays";
  return "working_days";
}

export default function Attendance() {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const [companies, setCompanies] = useState([]);
  // L'amministratore usa l'azienda di lavoro globale; gli altri ruoli
  // restano vincolati all'azienda assegnata dal proprio account.
  const [companyId, setCompanyId] = useCompanySelection();
  const [employees, setEmployees] = useState([]);
  // C3-B.2.5: per role=employee NON usiamo mai un employee_id proveniente dal browser.
  // L'id viene ricavato dalla risposta di GET /employees, che il backend auto-scopa
  // al collegamento autenticato (users.employee_id lato DB). Fino a quando la lista
  // non è caricata employeeId resta vuoto → UI in stato di caricamento.
  const [employeeId, setEmployeeId] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [monthData, setMonthData] = useState(null);
  const [rows, setRows] = useState({});
  const durFmt = useDurationFormat();  // M-UX-05 · formato durate dell'azienda attiva
  const [monthLoading, setMonthLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [, setCauseCatalogRevision] = useState(0);
  const [causeCatalog, setCauseCatalog] = useState([]);
  const [optionalCauseCodes, setOptionalCauseCodes] = useState([]);
  const [settlementContext, setSettlementContext] = useState(null);
  const [settlementSaving, setSettlementSaving] = useState(false);
  const [settlementSplitOpen, setSettlementSplitOpen] = useState(false);
  const [settlementSplit, setSettlementSplit] = useState({ exf: "", rol: "" });
  const [rangeOpen, setRangeOpen] = useState(false);
  const [rangeSaving, setRangeSaving] = useState(false);
  const [rangeData, setRangeData] = useState({
    start_date: "", end_date: "", cause: "FERIE",
    use_profile_hours: true, range_day_mode: "working_days", notes: "",
  });
  const [searchParams] = useSearchParams();
  // C3-B.2: stato del workflow mensile per abilitare/disabilitare mutation UI
  const [workflowState, setWorkflowState] = useState(null);
  const [workflowDoc, setWorkflowDoc] = useState(null);
  const [workflowRefresh, setWorkflowRefresh] = useState(0);
  // v1.5.4 · Le operazioni avanzate del cartellino sono ora integrate nel
  // menu "Altre operazioni" (attendance-more-actions-menu): un solo punto
  // di accesso per le funzioni non quotidiane. Il pulsante "Strumenti" e'
  // stato eliminato.
  const [cessationOpen, setCessationOpen] = useState(false);
  const [cessationSaving, setCessationSaving] = useState(false);
  const [cessationData, setCessationData] = useState({
    cessation_date: "",
    cessation_reason: "",
    note: "",
  });
  const isEmployeeRole = effectiveRole === "employee";
  const isExternalConsultant = effectiveRole === "external_consultant";
  // C3-B.2.7: employee è OPERATORE del proprio cartellino.
  // Può modificare in stato "draft" (Bozza) e "to_correct" (Da correggere).
  // In sent_to_company / approved / closed torna in sola lettura.
  const monthEditable = !isExternalConsultant && (workflowState == null || isEditableState(workflowState));
  const handleWorkflowStateChange = React.useCallback((state, doc) => {
    setWorkflowState(state);
    if (doc) {
      setWorkflowDoc(doc);
      if (doc.finalized_cessation) load();
    }
  }, [employeeId, year, month]);
  const dirtyRef = useRef(false);
  // Foundation Context Engine · registra il form come "dirty" quando ci sono
  // modifiche non salvate al cartellino: il cambio contesto aprira'
  // UnsavedChangesDialog.
  const [_isDirty, _setIsDirty] = useState(false);
  useEffect(() => {
    // Polling breve del ref (piu' semplice di rendering-driven, e la
    // finestra di apertura del dialog non e' latency-critica).
    const id = setInterval(() => {
      if (!!dirtyRef.current !== _isDirty) _setIsDirty(!!dirtyRef.current);
    }, 500);
    return () => clearInterval(id);
  }, [_isDirty]);
  useDirtyRegistrar("attendance-timesheet-form", _isDirty, {
    label: "Cartellino presenze in modifica",
    onDiscard: () => { dirtyRef.current = false; },
  });
  const stickyWorkbarRef = useRef(null);
  const [stickyWorkbarHeight, setStickyWorkbarHeight] = useState(0);
  // v1.1.1 · UX Mobile · sticky compact header + Sheet Dettagli
  //   Copre smartphone (< md) e tablet in verticale/landscape stretto
  //   (< lg = 1024 px), fascia in cui la toolbar desktop risulta
  //   troppo densa per essere usata comodamente col pollice.
  const isCompact = useIsCompact();
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);
  const [mobileEmpPickerOpen, setMobileEmpPickerOpen] = useState(false);
  const [bulkCloseOpen, setBulkCloseOpen] = useState(false);
  const [bulkCloseCount, setBulkCloseCount] = useState({ eligible: 0, already_closed: 0 });
  const [bulkClosing, setBulkClosing] = useState(false);
  // v0.99.30-hotfix-p0 (FIX4): race-guard token per fetch employees + load.
  // Ogni cambio azienda incrementa il token; le risposte con token stale
  // vengono scartate → nessuna contaminazione asincrona tra aziende.
  const employeesFetchTokenRef = useRef(0);
  const loadTokenRef = useRef(0);
  // Flag per distinguere il primo mount (rispetta URL empQP) dai cambi
  // azienda successivi (auto-selezione del primo dipendente).
  const isFirstCompanyEffectRef = useRef(true);
  // Dipendente richiesto da ?employee=: resta in attesa finché non è selezionato nella sua azienda
  // (l'accesso è verificato dal backend, che restituisce solo dipendenti autorizzati).
  const requestedEmployeeRef = useRef(searchParams.get("employee"));

  const optionalCauseChoices = useMemo(() => {
    const bankHourMarkerCodes = new Set([
      "STRAORDINARI_BANCA_ORE",
      "ROL_BANCA_ORE",
      "EX_FESTIVITA_BANCA_ORE",
    ]);
    const configured = causeCatalog
      .map((cause) => cause.code)
      .filter((code) => code && !ATTENDANCE_CORE_CAUSES.includes(code)
        && !["FESTIVITA", "SANTO_PATRONO"].includes(code)
        && !bankHourMarkerCodes.has(code));
    return Array.from(new Set([...ATTENDANCE_OPTIONAL_CAUSES, ...configured]))
      .filter((code) => CAUSE_LABELS[code] && !bankHourMarkerCodes.has(code));
  }, [causeCatalog]);

  const visibleCauseCodes = useMemo(() => {
    const bankHourMarkerCodes = new Set([
      "STRAORDINARI_BANCA_ORE",
      "ROL_BANCA_ORE",
      "EX_FESTIVITA_BANCA_ORE",
    ]);
    return [
      ...ATTENDANCE_CORE_CAUSES,
      ...optionalCauseCodes.filter((code) => (
        !ATTENDANCE_CORE_CAUSES.includes(code) && !bankHourMarkerCodes.has(code)
      )),
    ];
  }, [optionalCauseCodes]);

  useEffect(() => {
    if (!employeeId) {
      setOptionalCauseCodes([]);
      return;
    }
    try {
      const saved = JSON.parse(localStorage.getItem(`attendance_optional_causes_${employeeId}`) || "[]");
      setOptionalCauseCodes(Array.isArray(saved) ? saved : []);
    } catch (_) { setOptionalCauseCodes([]); }
  }, [employeeId]);

  useEffect(() => {
    const used = new Set();
    Object.values(rows || {}).forEach((row) => {
      Object.entries(row?.cause_hours || {}).forEach(([code, value]) => {
        if (!ATTENDANCE_CORE_CAUSES.includes(code) && durationToHours(value, durFmt) !== 0) used.add(code);
      });
      Object.entries(row?.cause_units || {}).forEach(([code, meta]) => {
        if (!ATTENDANCE_CORE_CAUSES.includes(code) && Number(meta?.qty || 0) !== 0) used.add(code);
      });
    });
    if (used.size) setOptionalCauseCodes((current) => Array.from(new Set([...current, ...used])));
  }, [rows, durFmt]);

  useEffect(() => {
    if (!employeeId) return;
    try { localStorage.setItem(`attendance_optional_causes_${employeeId}`, JSON.stringify(optionalCauseCodes)); } catch (_) { /* noop */ }
  }, [employeeId, optionalCauseCodes]);

  useEffect(() => {
    const element = stickyWorkbarRef.current;
    if (!element) return undefined;
    const measure = () => setStickyWorkbarHeight(
      Math.ceil(element.getBoundingClientRect().height)
    );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // Vista compatta (Griglia) vs Chip (dropdown). Preferenza in localStorage.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem("attendance_view_mode");
      return saved === "dropdown" ? "dropdown" : "compact";
    } catch (_) { return "compact"; }
  });
  useEffect(() => {
    try { localStorage.setItem("attendance_view_mode", viewMode); } catch (_) { /* noop */ }
  }, [viewMode]);

  useEffect(() => {
    const empQP = searchParams.get("employee");
    const yQP = searchParams.get("year");
    const mQP = searchParams.get("month");
    if (yQP) setYear(Number(yQP));
    if (mQP) setMonth(Number(mQP));
    // C3-B.2.5: per role=employee ignoriamo qualunque employee_id proveniente
    // dalla query string (impedisce spoofing lato browser). L'id verrà popolato
    // solo dal fetch backend auto-scoped.
    if (empQP && effectiveRole !== "employee") setEmployeeId(empQP);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data: causeRows } = await api.get("/causes");
        setCauseCatalog(hydrateCauseCatalog(causeRows));
        setCauseCatalogRevision((value) => value + 1);
        if (["admin", "super_admin"].includes(effectiveRole)) {
          const { data } = await api.get("/companies");
          const sorted = sortCompaniesIt(data);
          setCompanies(sorted);
          if (!companyId && sorted.length && !requestedEmployeeRef.current) setCompanyId(sorted[0].id);
        }
      } catch (e) { toast.error(apiErr(e)); }
    })();
  }, []);

  useEffect(() => {
    // v0.99.30-hotfix-p0 (FIX4): cambio azienda → clear immediato + fetch
    // con race-guard + auto-select del PRIMO dipendente attivo.
    // Sul PRIMO mount rispetta URL empQP (via reconcile) invece di auto-select.
    const firstMount = isFirstCompanyEffectRef.current;
    isFirstCompanyEffectRef.current = false;
    const myToken = ++employeesFetchTokenRef.current;

    // 1) Clear stato immediato per aziende SUCCESSIVE (no residui visibili).
    //    Sul primo mount NON azzeriamo per rispettare l'URL empQP.
    if (!firstMount) {
      // Invalida anche il load in corso: il prossimo effect [employeeId] userà
      // un nuovo token, e comunque employeeId torna a "" quindi load() esce.
      loadTokenRef.current += 1;
      if (effectiveRole !== "employee") {
        setEmployeeId("");
      }
      setEmployees([]);
      setMonthData(null);
      setRows({});
    }

    (async () => {
      try {
        if (effectiveRole === "employee") {
          // C3-B.2.5: fetch autoritativo dal backend. GET /employees è auto-scoped
          // dal server (routes/employees.py:216 → q["id"] = user.employee_id).
          // Nessun employee_id arbitrario dal browser viene accettato.
          const { data } = await api.get("/employees", { params: { status: "all" } });
          if (employeesFetchTokenRef.current !== myToken) return;
          const list = Array.isArray(data) ? data : [];
          setEmployees(list);
          if (list.length > 0) {
            setEmployeeId(list[0].id);
            // C3-B.2.3: popola companyId dal record autoritativo (fallback su user.company_id)
            if (!companyId && list[0].company_id) setCompanyId(list[0].company_id);
          } else {
            setEmployeeId("");
          }
          return;
        }
        const params = { status: "all" };  // v0.99.29 Fase 2: filtra client-side per periodo
        if (["admin", "super_admin"].includes(effectiveRole) && companyId) params.company_id = companyId;
        const { data } = await api.get("/employees", { params });
        // Race-guard: se un altro cambio azienda è in corso, scarta questa risposta.
        if (employeesFetchTokenRef.current !== myToken) return;
        setEmployees(data);
        const wanted = requestedEmployeeRef.current;
        if (wanted) {
          const hit = data.find((e) => e.id === wanted);
          let wantedCompany = hit?.company_id;
          if (!hit && ["admin", "super_admin"].includes(effectiveRole)) {
            wantedCompany = await api.get(`/employees/${wanted}`).then((r) => r.data?.company_id).catch(() => null);
            if (employeesFetchTokenRef.current !== myToken) return;
          }
          if (wantedCompany && wantedCompany !== companyId) { setCompanyId(wantedCompany); return; }
          requestedEmployeeRef.current = null;
          if (hit) { setEmployeeId(wanted); return; }
        }
        if (firstMount) {
          // Primo mount: rispetta URL empQP (via reconcile).
          // C3-B.2.6: se empQP non è presente/valido, auto-select del primo
          // dipendente attivo autorizzato — garantisce badge/actions visibili
          // per studio_operator/company_admin/company_responsible che non
          // arrivano dall'URL con employee query param.
          const nextId = ((prev) => {
            const reconciled = reconcileEmployeeWithCompany(prev, data);
            if (reconciled) return reconciled;
            const activeList = filterActiveInMonth(data, year, month)
              .filter((e) => !["admin", "super_admin"].includes(effectiveRole) || !companyId || e.company_id === companyId);
            return activeList.length > 0 ? activeList[0].id : "";
          })(employeeId);
          setEmployeeId(nextId);
          // C3-B.2.6: popola companyId per non-admin (studio_operator/
          // company_admin/company_responsible) dal record autoritativo,
          // necessario per MonthWorkflowControl (che richiede companyId).
          if (!companyId && !["admin", "super_admin"].includes(effectiveRole) && nextId) {
            const emp = data.find((e) => e.id === nextId);
            if (emp?.company_id) setCompanyId(emp.company_id);
          }
        } else {
          // Cambio azienda: auto-select del primo dipendente attivo nel mese.
          const activeList = filterActiveInMonth(data, year, month)
            .filter((e) => !["admin", "super_admin"].includes(effectiveRole) || !companyId || e.company_id === companyId);
          setEmployeeId(activeList.length > 0 ? activeList[0].id : "");
        }
      } catch (e) {
        if (employeesFetchTokenRef.current === myToken) toast.error(apiErr(e));
      }
    })();
  }, [companyId]);

  // v0.99.30-hotfix-p0: `cleanCauseHours` normalizza per SAVE.
  // - Accetta virgola O punto (previene salvataggio di draft "4,")
  // - Rifiuta stringhe non parsabili (blocca il salvataggio silenzioso)
  // - Applica arrotondamento matematico HALF_UP a 2 decimali
  // Ritorna `{ok: bool, cleaned: {}, error: string|null}` così il caller
  // può bloccare il POST se ci sono valori invalidi.
  // M-UX-05 · conversione/validazione tramite Foundation Durate (formato dell'azienda).
  const cleanCauseHours = (ch) => {
    const out = {};
    let error = null;
    Object.entries(ch || {}).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) return;
      const parsed = parseDuration(v, durFmt);
      if (parsed.error || parsed.value === null) {
        error = `Valore non valido per ${k}: "${String(v).trim()}". ${parsed.error || ""}`.trim();
        return;
      }
      const n = parsed.value;
      // Le festività devono restare visibili anche quando cadono in un giorno
      // non lavorativo (teorico 0): in quel caso conserviamo esplicitamente
      // la causale a 0,00 invece di farla sparire al salvataggio/ricaricamento.
      if (n > 0 || k === "FESTIVITA" || k === "SANTO_PATRONO" || k === "FERIE") {
        out[k] = n;
      }
    });
    return { ok: !error, cleaned: out, error };
  };

  const load = async (requestedPeriod = null) => {
    // Congela il periodo richiesto all'inizio del caricamento. In questo modo
    // un cambio mese rapido non puo riutilizzare i valori di una render
    // precedente e mostrare ancora il cartellino del mese vecchio.
    const requestedEmployeeId = requestedPeriod?.employeeId ?? employeeId;
    const requestedYear = requestedPeriod?.year ?? year;
    const requestedMonth = requestedPeriod?.month ?? month;
    if (!requestedEmployeeId) return;
    // v0.99.30-hotfix-p0 (FIX4): race-guard su load. Se durante il fetch
    // l'utente cambia azienda/dipendente, la risposta stale viene scartata.
    const myToken = ++loadTokenRef.current;
    setMonthLoading(true);
    try {
      // FASE B · P3 · Parallelizzazione: `/attendance/month` e `/attendance/list`
      // non hanno dipendenze di dato fra loro. Eseguiti in parallelo con
      // Promise.all la latenza totale diventa il max(month, list) invece di
      // month + list (~130-170 ms risparmiati per ogni apertura cartellino).
      const [monthRes, listRes] = await Promise.all([
        api.get("/attendance/month", {
          params: {
            employee_id: requestedEmployeeId,
            year: requestedYear,
            month: requestedMonth,
          },
        }),
        api.get("/attendance/list", {
          params: {
            employee_id: requestedEmployeeId,
            year: requestedYear,
            month: requestedMonth,
          },
        }),
      ]);
      if (loadTokenRef.current !== myToken) return;
      const data = monthRes.data;
      setMonthData(data);
      const map = {};
      (data.daily || []).forEach((d) => {
        const c = cleanCauseHours(d.cause_hours);
        // v0.99.30-hotfix-p0 (FIX3): formatta ogni ora committata come "X,YY".
        const formatted = {};
        Object.entries(c.cleaned).forEach(([k, v]) => {
          formatted[k] = formatDuration(v, durFmt);
        });
        // Le festività provengono dal calendario nazionale/backend e devono
        // comparire anche sui giorni di riposo, senza richiedere Ctrl+F2. Non
        // sovrascriviamo mai causali già registrate dall'operatore.
        const holiday = d.holiday;
        if (holiday && Object.keys(formatted).length === 0) {
          const holidayCause = holiday.type === "local" ? "SANTO_PATRONO" : "FESTIVITA";
          formatted[holidayCause] = formatDuration(d.theoretical || 0, durFmt);
        }
        map[d.date] = {
          cause_hours: formatted,
          // Checkpoint 2: espone cause_units (es. ferie in gg) alla griglia.
          cause_units: d.cause_units || {},
          notes: d.notes || (
            holiday
              ? (holiday.type === "local" ? "Santo Patrono" : holiday.name)
              : ""
          ),
          status: d.status === "missing" || d.status === "rest" ? "draft" : d.status,
        };
      });
      const list = listRes;
      if (loadTokenRef.current !== myToken) return;
      list.data.forEach((r) => {
        if (map[r.date]) {
          map[r.date].id = r.id;
          if (r.cause_units && Object.keys(r.cause_units).length > 0) {
            map[r.date].cause_units = r.cause_units;
          }
          if (r.theoretical_override !== null && r.theoretical_override !== undefined) {
            // v0.99.30-hotfix-p0 (FIX3): formatta theoretical_override come "X,YY".
            map[r.date].theoretical_override = formatDuration(r.theoretical_override, durFmt);
          }
        }
      });
      setRows(map);
      dirtyRef.current = false;
    } catch (e) {
      if (loadTokenRef.current === myToken) toast.error(apiErr(e));
    } finally {
      if (loadTokenRef.current === myToken) setMonthLoading(false);
    }
  };
  useEffect(() => {
    // Clear immediato dei dati residui se employeeId diventa vuoto (empty state)
    if (!employeeId) {
      loadTokenRef.current += 1;  // invalida load in corso
      setMonthData(null);
      setRows({});
      setMonthLoading(false);
      return;
    }
    // Il selettore cambia subito etichetta. Anche la griglia deve cambiare
    // subito stato: non deve mai restare visibile il mese precedente mentre
    // il server prepara quello nuovo.
    loadTokenRef.current += 1;
    setMonthData(null);
    setRows({});
    setMonthLoading(true);
    load({ employeeId, year, month });
  }, [employeeId, year, month]);

  // v0.99.30-c2-close (C2.4): azione atomica "Imposta come sabato lavorativo".
  // Chiama POST /api/employees/{id}/schedule-exceptions/set-saturday-worked.
  // Se il backend risponde 409 "saturday_quota_reached", chiediamo conferma
  // per lo spostamento e ripetiamo con replace_old_date. Dopo il successo,
  // ricarica il mese (il teorico del sabato viene aggiornato via effect).
  const setSaturdayWorked = async (isoDate) => {
    if (!employeeId) return;
    try {
      const res = await api.post(
        `/employees/${employeeId}/schedule-exceptions/set-saturday-worked`,
        { date: isoDate }
      );
      if (res.data?.no_op) {
        toast.info(`${isoDate} è già il sabato lavorativo del mese.`);
        return;
      }
      toast.success(res.data?.action === "created"
        ? `Sabato ${isoDate} impostato come lavorativo.`
        : `Sabato lavorativo spostato a ${isoDate}.`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "saturday_quota_reached") {
        const existing = detail.existing || [];
        const oldDate = existing[0]?.date;
        if (!oldDate) { toast.error(detail.message || "Quota raggiunta"); return; }
        const ok = window.confirm(
          `Il limite mensile di ${detail.quota} sabato/i è già raggiunto.\n` +
          `Sabato lavorativo attuale: ${oldDate}\n` +
          `Confermi lo spostamento a ${isoDate}?`
        );
        if (!ok) return;
        try {
          await api.post(
            `/employees/${employeeId}/schedule-exceptions/set-saturday-worked`,
            { date: isoDate, replace_old_date: oldDate }
          );
          toast.success(`Sabato spostato: ${oldDate} → ${isoDate}`);
          await load();
        } catch (e2) {
          toast.error(apiErr(e2));
        }
      } else if (e?.response?.status === 422 && detail?.code === "no_variable_saturday") {
        toast.warning("Il profilo di questo dipendente non prevede sabati variabili.");
      } else if (e?.response?.status === 409 && detail?.code === "month_closed") {
        toast.error("Il mese è chiuso: non è possibile modificare il sabato lavorativo.");
      } else {
        toast.error(apiErr(e));
      }
    }
  };

  const removeSaturdayWorked = async (isoDate) => {
    if (!employeeId) return;
    const ok = window.confirm(
      `Rimuovere le ore teoriche dal sabato ${isoDate}?\n\n` +
      "Le presenze e le causali già registrate non verranno cancellate. " +
      "Potrai poi scegliere un altro sabato del mese."
    );
    if (!ok) return;
    try {
      const res = await api.get(
        `/employees/${employeeId}/schedule-exceptions`,
        { params: { year } }
      );
      const current = (res.data || []).find(
        (item) => item.date === isoDate && Number(item.hours || 0) > 0
      );
      // Non eliminiamo semplicemente l'eccezione positiva: se sulla giornata
      // esistono ore ordinarie, la quota mensile la selezionerebbe nuovamente
      // al reload. L'override esplicito a zero mantiene invece esclusa questa
      // data e consente di scegliere/spostare il sabato su un'altra data.
      await api.post(
        `/employees/${employeeId}/schedule-exceptions`,
        {
          date: isoDate,
          hours: 0,
          note: current?.id
            ? "Sabato variabile escluso dall'operatore"
            : "Sabato automatico escluso dall'operatore",
        }
      );
      toast.success(`Sabato ${isoDate} rimosso. Ora puoi sceglierne un altro.`);
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const setCause = (date, cause, val) => {
    if (!monthEditable) return;
    dirtyRef.current = true;
    // v0.99.30-hotfix-p0 (FIX3): store raw string during typing, format on
    // commit (blur/Enter/save). Il commit avviene in `commitCause` che
    // arrotonda a 2 decimali e formatta come "X,YY".
    setRows((prev) => {
      const cur = prev[date] || { cause_hours: {}, notes: "", status: "draft" };
      const ch = { ...cur.cause_hours };
      // Filtra caratteri illegali (solo cifre e separatori ammessi).
      const raw = sanitizeDurationTyping(val, durFmt);
      if (raw === "") delete ch[cause];
      else ch[cause] = raw;

      // Se cambia una causale di assenza, adegua immediatamente l'ordinario:
      // 8 teoriche - 8 ore L.104 = 0 ord.; 8 - 1,50 permesso = 6,50 ord.
      if (THEORETICAL_REDUCTION_CAUSES.has(cause)) {
        const day = (monthData?.daily || []).find((item) => item.date === date);
        const theoretical = cur.theoretical_override ?? day?.theoretical ?? 0;
        const balanced = rebalanceOrdinaryHours(ch, theoretical);
        const formatted = { ...balanced.causeHours };
        if (balanced.hasAbsence && balanced.ordinaryHours > 0) {
          formatted.ORDINARIO = _formatHoursIt(balanced.ordinaryHours);
        } else if (balanced.hasAbsence) {
          delete formatted.ORDINARIO;
        } else if (raw === "") {
          // Rimuovendo l'ultima assenza, ripristina il teorico come ordinario.
          const theoreticalNumber = durationToHours(theoretical || 0, durFmt);
          if (theoreticalNumber > 0) formatted.ORDINARIO = _formatHoursIt(theoreticalNumber);
        }
        return { ...prev, [date]: { ...cur, cause_hours: formatted } };
      }
      return { ...prev, [date]: { ...cur, cause_hours: ch } };
    });
  };
  const setTheoretical = (date, val) => {
    if (!monthEditable) return;
    dirtyRef.current = true;
    // v0.99.30-hotfix-p0 (FIX3): stessa logica di setCause.
    setRows((prev) => {
      const cur = prev[date] || { cause_hours: {}, notes: "", status: "draft" };
      const next = { ...cur };
      const raw = sanitizeDurationTyping(val, durFmt);
      if (raw === "") { delete next.theoretical_override; return { ...prev, [date]: next }; }
      next.theoretical_override = raw;
      return { ...prev, [date]: next };
    });
  };
  // v0.99.30-hotfix-p0 (FIX3): commitCause / commitTheoretical formattano al
  // blur/Invio/Salva. Regole:
  //   - "5"     → "5,00"
  //   - "5,4"   → "5,40"
  //   - "4.75"  → "4,75"
  //   - "4,754" → "4,75" (HALF_UP matematico)
  //   - "0"     → rimosso dalla mappa
  //   - "abc"   → NO commit (draft rimane invalido, il save bloccherà)
  const _formatHoursIt = (n) => formatDuration(roundDurationStorage(n, durFmt), durFmt);
  const commitCause = (date, cause) => {
    setRows((prev) => {
      const cur = prev[date];
      if (!cur) return prev;
      const v = cur.cause_hours?.[cause];
      if (v === undefined || v === null || v === "") return prev;
      const { value: n, error } = parseDuration(v, durFmt);
      if (error || n === null) return prev;
      const ch = { ...cur.cause_hours };
      if (n === 0 && cause !== "FERIE") { delete ch[cause]; }
      else { ch[cause] = _formatHoursIt(n); }
      return { ...prev, [date]: { ...cur, cause_hours: ch } };
    });
  };
  const commitTheoretical = (date) => {
    setRows((prev) => {
      const cur = prev[date];
      if (!cur || cur.theoretical_override === undefined || cur.theoretical_override === "") return prev;
      const { value: n, error } = parseDuration(cur.theoretical_override, durFmt);
      if (error || n === null) return prev;
      return { ...prev, [date]: { ...cur, theoretical_override: _formatHoursIt(n) } };
    });
  };
  // Focus movement al tasto Invio: cerca la prossima cella nella stessa
  // "colonna" (stessa causale o teoriche) nelle righe sotto. In vista Chip,
  // i chip esistono solo se la causale è attiva quel giorno: saltiamo le
  // righe in cui la cella target non esiste o è disabilitata. Sull'ultima
  // riga il focus resta dov'è senza errore.
  const focusNextInColumn = (currentDate, testidTemplate) => {
    const days = (monthData?.daily || []).filter((d) => {
      if (statusFilter === "all") return true;
      const cur = rows[d.date]?.status || d.status;
      return cur === statusFilter;
    });
    const idx = days.findIndex((d) => d.date === currentDate);
    if (idx < 0) return;
    for (let i = idx + 1; i < days.length; i++) {
      const tid = testidTemplate.replace("{date}", days[i].date);
      const el = document.querySelector(`[data-testid="${tid}"]`);
      if (el && !el.disabled && el.offsetParent !== null) {
        el.focus();
        if (typeof el.select === "function") el.select();
        return;
      }
    }
    // Ultima riga (o nessuna cella disponibile sotto): mantieni focus corrente.
  };
  // Navigazione completa della griglia con i quattro tasti freccia.
  // Le celle sono ordinate per giornata e per colonna (TEOR. + causali).
  // Prima di spostare il focus viene consolidato il valore corrente.
  const focusGridCell = (currentDate, currentColumn, key) => {
    if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(key)) return false;
    const days = (monthData?.daily || []).filter((d) => {
      if (statusFilter === "all") return true;
      const cur = rows[d.date]?.status || d.status;
      return cur === statusFilter;
    });
    const columns = ["__THEORETICAL__", ...visibleCauseCodes];
    let rowIndex = days.findIndex((d) => d.date === currentDate);
    let columnIndex = columns.indexOf(currentColumn);
    if (rowIndex < 0 || columnIndex < 0) return false;
    if (key === "ArrowUp") rowIndex -= 1;
    if (key === "ArrowDown") rowIndex += 1;
    if (key === "ArrowLeft") columnIndex -= 1;
    if (key === "ArrowRight") columnIndex += 1;
    if (rowIndex < 0 || rowIndex >= days.length || columnIndex < 0 || columnIndex >= columns.length) {
      return true;
    }
    const targetDate = days[rowIndex].date;
    const targetColumn = columns[columnIndex];
    const testid = targetColumn === "__THEORETICAL__"
      ? `att-teor-${targetDate}`
      : `att-${targetDate}-${targetColumn}`;
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (el && !el.disabled && el.offsetParent !== null) {
      el.focus();
      if (typeof el.select === "function") el.select();
    }
    return true;
  };
  const setNotes = (date, notes) => {
    if (!monthEditable) return;
    dirtyRef.current = true;
    setRows((prev) => ({ ...prev, [date]: { ...(prev[date] || { cause_hours: {}, status: "draft" }), notes } }));
  };

  const saveAll = async (newStatus = null) => {
    if (!monthEditable) {
      toast.error(`Mese in stato "${workflowState}" non modificabile. Riportarlo in Bozza o Da correggere per modificare.`);
      return false;
    }
    if (!employeeId) return false;
    setSaving(true);
    try {
      // v1.5.2 · PRINCIPIO ARCHITETTURALE DEFINITIVO:
      // Il pulsante SALVA salva ESCLUSIVAMENTE cio' che l'utente ha digitato.
      // Nessun autofill, nessuna compilazione automatica di giornate vuote,
      // nessun inserimento di ore ordinarie, MAI. L'automatismo del vecchio
      // "Salva ed elabora" e' stato eliminato definitivamente.
      // L'impostazione azienda `attendance_autofill_mode` NON influenza piu'
      // il salvataggio: viene usata solo come DEFAULT SUGGERITO nel dialog
      // "Strumenti" (badge "Suggerito" sulla tool piu' frequente).
      const effectiveRows = rows;
      const autoFilled = 0;
      // v0.99.30-hotfix-p0: valida ogni cause_hours prima di POST.
      // Se qualche valore è invalido (es. "abc", "4,,"), BLOCCA il salvataggio
      // con toast d'errore. Non correggere silenziosamente.
      const entries = [];
      for (const [date, v] of Object.entries(effectiveRows)) {
        // Un record già presente deve essere inviato anche quando l'operatore
        // ha cancellato il suo ultimo valore. In caso contrario il backend non
        // riceve la mappa vuota e al reload ricompare il vecchio dato.
        const hasContent = shouldPersistAttendanceRow(v);
        if (!hasContent) continue;
        const c = cleanCauseHours(v.cause_hours);
        if (!c.ok) {
          toast.error(`${date}: ${c.error}`);
          setSaving(false);
          return false;
        }
        // v0.99.30-hotfix-p0 (FIX2): normalizza theoretical_override (potrebbe
        // essere una stringa draft italiana come "4,75" o "4,").
        let teorOverride = v.theoretical_override;
        if (teorOverride !== undefined && teorOverride !== null && teorOverride !== "") {
          const rawT = String(teorOverride).trim();
          const pT = parseDuration(rawT, durFmt);
          if (pT.error || pT.value === null) {
            toast.error(`${date}: Ore teoriche non valide: "${rawT}". ${pT.error || ""}`.trim());
            setSaving(false);
            return false;
          }
          teorOverride = pT.value;
        }
        entries.push({
          // Il salvataggio deve rispettare l'ORDINARIO digitato manualmente.
          // Il ricalcolo automatico resta solo al cambio di un'assenza e in Ctrl+F2.
          // Checkpoint 2: mantiene invariate le ferie in gg (cause_units) gia'
          // presenti sul record, per non azzerarle al primo salvataggio Ctrl+F2.
          date, cause_hours: preserveManualHoursForSave(c.cleaned), theoretical_override: teorOverride,
          cause_units: v.cause_units && Object.keys(v.cause_units).length > 0 ? v.cause_units : undefined,
          notes: v.notes, status: newStatus || v.status || "draft",
        });
      }
      if (entries.length === 0) { toast.info("Nessuna presenza da salvare"); return true; }
      await api.post("/attendance/upsert", { employee_id: employeeId, entries });
      // v1.1.1 (P0.5): messaggio finale sintetico con riepilogo elaborazione.
      const parts = [`Salvate ${entries.length} presenze`];
      if (autoFilled > 0) parts.push(`${autoFilled} giornate compilate da teorico`);
      parts.push("ricalcoli completati");
      toast.success(parts.join(" · "));
      dirtyRef.current = false;
      load();
      return true;
    } catch (e) { toast.error(apiErr(e)); return false; }
    finally { setSaving(false); }
  };

  // Chiusura sicura: acquisisce le modifiche non salvate della griglia prima di concludere.
  const saveBeforeClose = async () => (dirtyRef.current ? saveAll() : true);

  const openBulkCloseDialog = async () => {
    try {
      const cid = ["admin", "super_admin"].includes(effectiveRole) ? (companyId || currentEmp?.company_id || undefined) : undefined;
      const { data } = await api.get("/workflow/months/bulk-close/preview", { params: { year, month, company_id: cid } });
      setBulkCloseCount(data || { eligible: 0, already_closed: 0 });
      setBulkCloseOpen(true);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const confirmBulkClose = async () => {
    if (bulkClosing) return;
    setBulkClosing(true);
    try {
      const cid = ["admin", "super_admin"].includes(effectiveRole) ? (companyId || currentEmp?.company_id || undefined) : undefined;
      if (!(await saveBeforeClose())) return;
      const { data } = await api.post("/workflow/months/bulk-close", { year, month, company_id: cid });
      const parts = [`${data.closed} lavorazioni concluse`];
      if (data.skipped > 0) parts.push(`${data.skipped} non concluse`);
      (data.skipped > 0 ? toast.warning : toast.success)(parts.join(" · "), { duration: data.skipped > 0 ? 12000 : 4000 });
      setBulkCloseOpen(false);
      setWorkflowRefresh((v) => v + 1);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === "closure_anomalies") {
        toast.error(formatClosureAnomalies(detail), { duration: 15000, className: "whitespace-pre-line", id: "closure-anomalies" });
      } else toast.error(apiErr(e));
    }
    finally { setBulkClosing(false); }
  };


  const bulkStatus = async (status) => {
    if (!monthEditable) {
      toast.error("Mese non modificabile in questo stato del workflow.");
      return;
    }
    const ids = Object.values(rows).map((r) => r.id).filter(Boolean);
    if (ids.length === 0) { toast.info("Nessuna presenza da aggiornare"); return; }
    try {
      await api.post("/attendance/status", { ids, status });
      toast.success(`Aggiornate ${ids.length} presenze`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  // v1.5.5 · Operazione di WORKFLOW: "Approva tutto" nel contesto sent_to_company
  // NON tocca lo status delle singole righe (in quello stato il mese e' bloccato
  // in scrittura), ma esegue la transizione workflow `approve` — cioe' la stessa
  // operazione del pulsante "Approva" del MonthWorkflowControl.
  // Aderisce alla linea guida architetturale: approvazione = operazione di
  // workflow, non di cartellino. Il pulsante toolbar rimane come "shortcut"
  // visibile solo quando c'e' un vero flusso di approvazione in corso
  // (dipendente → invio → approvazione), mai quando il consulente compila
  // direttamente (draft) o quando il mese e' gia' approvato/chiuso.
  const approveMonthWorkflow = async () => {
    if (!employeeId) return;
    if (workflowState !== "sent_to_company") {
      toast.error("Nessun cartellino da approvare in questo stato del workflow.");
      return;
    }
    try {
      const cid = currentEmp?.company_id || companyId;
      const st = await api.get("/workflow/months/state", {
        params: { company_id: cid, employee_id: employeeId, year, month },
      });
      const wf = st.data;
      await api.post("/workflow/months/transition", {
        workflow_id: wf.id,
        action: "approve",
        expected_version: wf.version,
      });
      setWorkflowState("approved");
      toast.success("Cartellino approvato");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  // v1.1.1 (P0.4): estratto il "compute puro" del prefill da teorico, così
  // che possa essere riutilizzato sia dall'azione manuale (bottone "Compila
  // dal teorico") sia dall'auto-compilazione eseguita da "Salva ed elabora".
  // Nessuna duplicazione di logica: entrambi i percorsi chiamano lo stesso
  // helper. La funzione NON scrive stato React (setRows) e NON emette toast.
  // v1.5.1 · Cartellino: risolve la modalita' di autofill dell'azienda.
  //   'ask' (default nuove aziende) / 'never' / 'always_theoretical' / 'always_profile'
  const _resolveAttendanceAutofillMode = () => {
    const cid = currentEmp?.company_id || companyId;
    const co = (companies || []).find((c) => c.id === cid);
    return co?.attendance_autofill_mode || "always_theoretical";
  };

  const _computeTheoreticalPrefill = (baseRows, dailyList, holidaysMap) => {
    const next = { ...baseRows };
    let filled = 0;
    (dailyList || []).forEach((day) => {
      const row = baseRows[day.date] || { cause_hours: {}, notes: "", status: "draft" };
      const holiday = holidaysMap.get(day.date);
      const hasHours = Object.values(row.cause_hours || {}).some((v) => {
        const n = durationToHours(v || "", durFmt);
        return Number.isFinite(n) && n > 0;
      });
      const theoretical = Number(day.theoretical || 0);
      if (theoretical <= 0 && !holiday) return;

      const dayUnits = row.cause_units || day.cause_units || {};
      const balanced = rebalanceOrdinaryHours(row.cause_hours, theoretical, dayUnits);
      if (balanced.hasAbsence) {
        const formatted = { ...balanced.causeHours };
        if (balanced.ordinaryHours > 0) {
          formatted.ORDINARIO = _formatHoursIt(balanced.ordinaryHours);
        } else {
          delete formatted.ORDINARIO;
        }
        next[day.date] = { ...row, cause_hours: formatted, cause_units: dayUnits, status: "draft" };
        filled += 1;
        return;
      }

      const hasDaysAbsence = Object.values(dayUnits).some((m) => {
        const q = Number(m && m.qty);
        return m && (String(m.unit).toLowerCase() === "gg") && Number.isFinite(q) && q > 0;
      });
      if (hasDaysAbsence) return;

      if (hasHours || row.notes || row.theoretical_override !== undefined) return;
      const cause = holiday
        ? (holiday.type === "local" ? "SANTO_PATRONO" : "FESTIVITA")
        : "ORDINARIO";
      next[day.date] = {
        ...row,
        cause_hours: { [cause]: _formatHoursIt(Math.max(0, theoretical)) },
        notes: holiday ? (holiday.type === "local" ? "Santo Patrono" : holiday.name) : row.notes,
        status: "draft",
      };
      filled += 1;
    });
    return { next, filled };
  };

  const _fetchMonthHolidays = async () => {
    const cid = currentEmp?.company_id || companyId;
    try {
      const { data } = await api.get("/guided/holidays", { params: { year, company_id: cid } });
      return new Map(
        (data?.holidays || [])
          .filter((h) => h.date?.startsWith(`${year}-${String(month).padStart(2, "0")}-`))
          .map((h) => [h.date, h]),
      );
    } catch {
      return new Map();
    }
  };

  const prefillFromTheoretical = async () => {
    if (!employeeId || !monthEditable || !monthData?.daily) return;
    const holidays = await _fetchMonthHolidays();
    const { next, filled } = _computeTheoreticalPrefill(rows, monthData.daily, holidays);
    if (filled > 0) {
      setRows(next);
      dirtyRef.current = true;
      toast.success(`Compilate ${filled} giornate vuote. Controlla e salva.`);
    } else {
      toast.info("Nessuna giornata vuota da compilare.");
    }
  };

  const clearWholeMonth = async () => {
    if (!employeeId || !monthEditable) return;
    const label = `${MONTHS_IT[month - 1]} ${year}`;
    const employee = currentEmp ? `${currentEmp.last_name} ${currentEmp.first_name}` : "il dipendente";
    if (!window.confirm(
      `Confermi lo svuotamento completo di ${label} per ${employee}?\n\n` +
      "Saranno eliminate soltanto le presenze del mese. Anagrafica e profili orari resteranno invariati."
    )) return;
    try {
      const { data } = await api.delete("/attendance/month", {
        params: { employee_id: employeeId, year, month },
      });
      toast.success(`Mese svuotato: eliminate ${data?.deleted || 0} registrazioni.`);
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const copyTheoreticalToFocusedCell = () => {
    if (!monthEditable) {
      toast.error("Il mese è in sola lettura: riaprilo per poter usare F9.");
      return;
    }

    const focusedTestId = document.activeElement?.dataset?.testid || "";
    const target = parseAttendanceCauseCellTestId(focusedTestId);
    if (!target) {
      toast.info("Seleziona prima una cella causale della griglia.");
      return;
    }

    const currentValue = rows[target.date]?.cause_hours?.[target.cause];
    if (
      currentValue !== undefined
      && currentValue !== null
      && String(currentValue).trim() !== ""
    ) {
      toast.warning("La cella contiene già un valore: F9 non lo sovrascrive.");
      return;
    }

    const day = (monthData?.daily || []).find(
      (item) => item.date === target.date,
    );
    const theoretical = parseAttendanceHours(
      rows[target.date]?.theoretical_override ?? day?.theoretical,
    );
    if (theoretical <= 0) {
      toast.info("In questa giornata le ore teoriche sono 0,00.");
      return;
    }

    const formatted = _formatHoursIt(theoretical);
    setCause(target.date, target.cause, formatted);
    const nextCellTemplate = focusedTestId.startsWith("att-chip-input-")
      ? `att-chip-input-{date}-${target.cause}`
      : `att-{date}-${target.cause}`;
    // Dopo la copia porta il cursore alla cella immediatamente sotto nella
    // stessa colonna, mantenendo lo stesso flusso operativo del tasto Invio.
    window.requestAnimationFrame(() => {
      focusNextInColumn(target.date, nextCellTemplate);
    });
    toast.success(
      `${formatted} ore copiate nella cella selezionata. Salva le bozze per confermare.`,
    );
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "F2") {
        event.preventDefault();
        prefillFromTheoretical();
      } else if (event.key === "F9") {
        event.preventDefault();
        copyTheoreticalToFocusedCell();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  // C3-B.2: chiusura/riapertura mese passano esclusivamente attraverso il
  // componente MonthWorkflowControl → POST /api/workflow/months/transition.
  // Le route legacy /api/legacy-imports/month/(close|reopen) sono state
  // rimosse dalla UI (backend ritorna 410).


  const totals = monthData?.totals || {};
  const balance = monthData?.month_balance || 0;
  const progressive = monthData?.progressive_balance || 0;

  const filteredDays = (monthData?.daily || []).filter((d) => {
    if (statusFilter === "all") return true;
    const cur = rows[d.date]?.status || d.status;
    return cur === statusFilter;
  });

  const currentEmp = employees.find((e) => e.id === employeeId);

  const refreshSettlementContext = React.useCallback(async () => {
    if (!employeeId) { setSettlementContext(null); return; }
    try {
      const { data } = await api.get("/attendance/bank-hour-settlement", {
        params: { employee_id: employeeId, year, month },
      });
      setSettlementContext(data);
    } catch (error) {
      // 409 significa semplicemente che non siamo nel mese di chiusura o che
      // la banca ore non e' attiva: nessun avviso rumoroso durante la navigazione.
      if (error?.response?.status !== 409) console.error(error);
      setSettlementContext(null);
    }
  }, [employeeId, year, month]);

  // FASE B · P3 · Parallelizzazione: la chiamata a /bank-hour-settlement non
  // dipende dai dati di /attendance/month, quindi la lanciamo in parallelo
  // allo stesso ciclo [employeeId, year, month] invece di aspettare
  // monthData.progressive_balance. Dopo mutazioni esplicite (POST/DELETE
  // settlement) refreshSettlementContext() viene comunque ri-chiamato in
  // modo diretto.
  useEffect(() => { refreshSettlementContext(); }, [refreshSettlementContext]);

  const toggleOptionalCause = (code) => {
    setOptionalCauseCodes((current) => current.includes(code)
      ? current.filter((item) => item !== code)
      : [...current, code]);
  };

  const saveSemesterSettlement = async (settlementType) => {
    const residual = Number(settlementContext?.residual_before_settlement || 0);
    const amount = fmtHours(Math.abs(residual));
    const isCessation = settlementContext?.closure_reason === "employment_cessation";
    const labels = {
      STR_BO: "liquidare come straordinario",
      ROL_BO: "recuperare dal contatore ROL/Permessi",
      EXF_BO: "recuperare dal contatore Ex Festivita",
    };
    if (!window.confirm(
      `Conguaglio banca ore ${settlementContext?.period_start} - ${settlementContext?.period_end}\n\n` +
      `Residuo: ${fmtHoursSigned(residual)} ore\nOperazione: ${labels[settlementType]} (${amount} ore)\n\n` +
      (isCessation
        ? "Confermi l'azzeramento del saldo banca ore per cessazione del rapporto?"
        : "Confermi l'azzeramento del saldo semestrale?")
    )) return;
    setSettlementSaving(true);
    try {
      await api.post("/attendance/bank-hour-settlement", {
        employee_id: employeeId, year, month, settlement_type: settlementType,
      });
      toast.success(isCessation
        ? "Conguaglio di cessazione registrato. Saldo banca ore azzerato."
        : "Conguaglio semestrale registrato. Saldo banca ore azzerato.");
      await load({ employeeId, year, month });
      await refreshSettlementContext();
    } catch (error) { toast.error(apiErr(error)); }
    finally { setSettlementSaving(false); }
  };

  const parseSettlementHours = (value) => {
    const { value: parsed, error } = parseDuration(String(value ?? ""), durFmt);
    return error || parsed === null ? NaN : parsed;
  };

  const openMixedSettlement = () => {
    const required = Math.abs(Number(settlementContext?.residual_before_settlement || 0));
    const exfAvailable = Number(settlementContext?.exf_available || 0);
    // Business rule GI Presenze (v1.3.2): nessun blocco di capienza. La
    // banca ore deve sempre essere liquidabile. Proposta operativa: consuma
    // prima le Ex Festivita disponibili (anche parzialmente), il resto va
    // su ROL. Entrambe le quote restano editabili dall'operatore.
    const exf = Math.max(0, Math.min(Math.max(0, exfAvailable), required));
    const rol = Math.max(0, required - exf);
    setSettlementSplit({
      exf: _formatHoursIt(exf),
      rol: _formatHoursIt(rol),
    });
    setSettlementSplitOpen(true);
  };

  const saveMixedSettlement = async () => {
    const required = Math.round(
      Math.abs(Number(settlementContext?.residual_before_settlement || 0)) * 100
    ) / 100;
    const exfAmount = parseSettlementHours(settlementSplit.exf);
    const rolAmount = parseSettlementHours(settlementSplit.rol);
    // Business rule GI Presenze (v1.3.2): accettate quote 0 su una delle
    // due colonne. Unica validazione: somma == residuo. Nessun blocco di
    // capienza (pool possono scendere sotto zero).
    if (!Number.isFinite(exfAmount) || !Number.isFinite(rolAmount) || exfAmount < 0 || rolAmount < 0) {
      toast.error("Inserisci entrambe le quote (anche zero), non negative.");
      return;
    }
    if (Math.abs((exfAmount + rolAmount) - required) > 0.01) {
      toast.error(`La somma deve essere ${fmtHours(required)} ore.`);
      return;
    }
    setSettlementSaving(true);
    try {
      await api.post("/attendance/bank-hour-settlement", {
        employee_id: employeeId,
        year,
        month,
        settlement_type: "MIXED_BO",
        exf_amount: exfAmount,
        rol_amount: rolAmount,
      });
      setSettlementSplitOpen(false);
      toast.success(settlementContext?.closure_reason === "employment_cessation"
        ? "Conguaglio di cessazione ripartito. Saldo banca ore azzerato."
        : "Conguaglio ripartito registrato. Saldo banca ore azzerato.");
      await load({ employeeId, year, month });
      await refreshSettlementContext();
    } catch (error) { toast.error(apiErr(error)); }
    finally { setSettlementSaving(false); }
  };

  const reverseSemesterSettlement = async () => {
    if (!window.confirm("Annullare il conguaglio semestrale e ripristinare il saldo precedente?")) return;
    setSettlementSaving(true);
    try {
      await api.delete("/attendance/bank-hour-settlement", {
        params: { employee_id: employeeId, year, month },
      });
      toast.success("Conguaglio annullato.");
      await load({ employeeId, year, month });
      await refreshSettlementContext();
    } catch (error) { toast.error(apiErr(error)); }
    finally { setSettlementSaving(false); }
  };

  const openCessationDialog = async () => {
    if (!employeeId || !monthEditable) return;
    try {
      const cid = currentEmp?.company_id || companyId;
      const { data } = await api.get("/workflow/months/state", {
        params: { company_id: cid, employee_id: employeeId, year, month },
      });
      setWorkflowDoc(data);
      const existingDate = data.pending_cessation_date || (
        currentEmp?.cessation_date?.startsWith(
          `${year}-${String(month).padStart(2, "0")}-`
        ) ? currentEmp.cessation_date : ""
      );
      setCessationData({
        cessation_date: existingDate,
        cessation_reason: data.pending_cessation_reason || currentEmp?.cessation_reason || "",
        note: data.pending_cessation_note || "",
      });
      setCessationOpen(true);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const savePendingCessation = async () => {
    if (!cessationData.cessation_date || !cessationData.cessation_reason) {
      toast.error("Indica la data e il motivo della cessazione");
      return;
    }
    setCessationSaving(true);
    try {
      const cid = currentEmp?.company_id || companyId;
      const fresh = await api.get("/workflow/months/state", {
        params: { company_id: cid, employee_id: employeeId, year, month },
      });
      const { data } = await api.post("/workflow/months/cessation", {
        company_id: cid,
        employee_id: employeeId,
        year,
        month,
        ...cessationData,
        expected_version: fresh.data.version,
      });
      setWorkflowDoc(data);
      setWorkflowRefresh((value) => value + 1);
      setCessationOpen(false);
      await load();
      const records = data.post_cessation_records?.length || 0;
      toast.success(
        records
          ? `Cessazione predisposta. ${records} giornate successive restano nello storico ma non entrano nei calcoli.`
          : "Cessazione predisposta. Sarà registrata con Concludi cartellino."
      );
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setCessationSaving(false);
    }
  };

  const clearPendingCessation = async () => {
    if (!workflowDoc?.id || !workflowDoc?.pending_cessation_date) return;
    if (!window.confirm("Annullare la cessazione predisposta per questo mese?")) return;
    setCessationSaving(true);
    try {
      await api.delete("/workflow/months/cessation", {
        params: {
          workflow_id: workflowDoc.id,
          expected_version: workflowDoc.version,
        },
      });
      setWorkflowDoc((doc) => ({
        ...doc,
        pending_cessation_date: null,
        pending_cessation_reason: null,
        pending_cessation_note: null,
        version: doc.version + 1,
      }));
      setWorkflowRefresh((value) => value + 1);
      await load();
      toast.success("Cessazione predisposta annullata");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setCessationSaving(false);
    }
  };
  useEffect(() => {
    announceShellCompany(
      currentEmp?.company_name || user?.company_name || companyNameForShell(companies, companyId),
      companyId,
    );
  }, [companies, companyId, currentEmp?.company_name, user?.company_name]);

  // C3-B.2.5: per role=employee mostriamo sempre il nome del dipendente
  // collegato al proprio account (ricavato via backend). Se la lista è ancora
  // in caricamento mostriamo "Caricamento…" invece del placeholder generico.
  let empDisplay;
  if (currentEmp) {
    empDisplay = `${currentEmp.last_name} ${currentEmp.first_name}`;
  } else if (isEmployeeRole) {
    empDisplay = "Caricamento…";
  } else {
    empDisplay = "Nessun dipendente selezionato";
  }

  const operationalCauses = causeCatalog.filter(
    (cause) => !["ORDINARIO", "FESTIVITA", "SANTO_PATRONO"].includes(cause.code)
  );

  const openRangeDialog = () => {
    const firstDate = `${year}-${String(month).padStart(2, "0")}-01`;
    const selectedCause = operationalCauses.find((cause) => cause.code === "FERIE")
      || operationalCauses[0];
    setRangeData({
      start_date: firstDate,
      end_date: firstDate,
      cause: selectedCause?.code || "FERIE",
      use_profile_hours: true,
      range_day_mode: causeRangeDayMode(selectedCause),
      notes: "",
    });
    setRangeOpen(true);
  };

  const saveRange = async () => {
    if (!employeeId) { toast.error("Seleziona il dipendente"); return; }
    if (!rangeData.start_date || !rangeData.end_date) { toast.error("Indica Dal e Al"); return; }
    if (rangeData.end_date < rangeData.start_date) {
      toast.error("La data finale deve essere uguale o successiva alla data iniziale");
      return;
    }
    setRangeSaving(true);
    try {
      const { data } = await api.post("/guided/exception-range", {
        employee_id: employeeId,
        start_date: rangeData.start_date,
        end_date: rangeData.end_date,
        cause: rangeData.cause,
        use_profile_hours: true,
        range_day_mode: rangeData.range_day_mode,
        notes: rangeData.notes || null,
        replace_existing: true,
      });
      setRangeOpen(false);
      toast.success(
        `Periodo registrato: ${data.created + data.updated} giornate`
        + (data.calendar_days ? ` · ${data.calendar_days} giorni a zero ore` : "")
        + (data.skipped ? ` · ${data.skipped} giorni esclusi` : "")
      );
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setRangeSaving(false);
    }
  };

  return (
    <div className="flex gap-0 min-w-0" data-testid="presenze-page-shell">
    <div className="flex-1 min-w-0 space-y-2 max-w-[1280px]" data-testid="presenze-page">
      {/* v1.5.10 (Sprint V3.1) · Header pagina — Foundation WorkspaceHeader
          Enterprise Workspace (rif. audit/SPRINT_V3_0B_ENTERPRISE_WORKSPACE.md).
          Subject-centric: H1 nomina la persona ("Cartellino di {Nome}").
          Preserva `attendance-header-employee` e `attendance-header-company`. */}
      <WorkspaceHeader
        testid="attendance-workspace-header"
        eyebrow="GI Presenze · Cartellino"
        title={
          <>
            <span className="text-suite-neutral-500 font-normal">Cartellino di </span>
            <span data-testid="attendance-header-employee" title={empDisplay}>
              {empDisplay}
            </span>
          </>
        }
        subtitle={
          isEmployeeRole && currentEmp?.company_name ? (
            <span data-testid="attendance-header-company">{currentEmp.company_name}</span>
          ) : (
            `${MONTHS_IT[month - 1]} ${year}`
          )
        }
      />

      {/* v0.99.30-c2-close (C2.3 final): il controllo mese è ora integrato
          nell'info-strip filtri sottostante (spazio centrale dopo Stato).
          Rimossa la riga autonoma per non aumentare l'altezza del pannello. */}

      {/* INFO-STRIP UNICA: filtri + KPI inline + toggle vista + hint centesimi (icona ⓘ) */}
      <div ref={stickyWorkbarRef}
           className="sticky top-0 z-40 space-y-2 bg-suite-neutral-50 py-1 before:pointer-events-none before:absolute before:inset-x-0 before:bottom-full before:h-16 before:bg-suite-neutral-50"
           data-testid="attendance-sticky-workbar">
        {/* v1.1.1 · Barra compatta mobile/tablet — componente Design System
            `CompactMobileHeader`. Documentato in /app/docs/design-system/README.md.
            L'implementazione precedente era inline; ora attraversa lo standard
            condiviso Suite. Zero modifiche di comportamento. */}
        <div className="lg:hidden" data-testid="attendance-mobile-compact-bar">
          <CompactMobileHeader
            testId="attendance-mobile"
            context={{
              label: `${MONTHS_IT[month - 1]?.slice(0, 3)}. ${String(year).slice(-2)}`,
              ariaLabel: "mese",
              onClick: () => setMobileDetailsOpen(true),
              onPrev: () => { const nm = month === 1 ? 12 : month - 1; const ny = month === 1 ? year - 1 : year; setMonth(nm); setYear(ny); },
              onNext: () => { const nm = month === 12 ? 1 : month + 1; const ny = month === 12 ? year + 1 : year; setMonth(nm); setYear(ny); },
            }}
            entity={effectiveRole !== "employee" ? {
              label: currentEmp ? `${currentEmp.last_name} ${currentEmp.first_name?.[0] || ""}.` : "Seleziona…",
              title: currentEmp ? `${currentEmp.last_name} ${currentEmp.first_name}` : "Dipendente",
              onClick: () => setMobileEmpPickerOpen(true),
            } : null}
            primary={!isExternalConsultant ? {
              label: "Salva",
              ariaLabel: "Salva cartellino (solo salvataggio, senza autofill)",
              onClick: () => saveAll(),
              disabled: !employeeId || !monthEditable,
              loading: saving,
            } : undefined}
            secondary={{ onClick: () => setMobileDetailsOpen(true) }}
          />
        </div>
        {/* === Contenuto workbar originale (visibile ≥ md) === */}
        <div className="hidden lg:block space-y-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-suite-neutral-200 bg-suite-surface px-3 py-2 text-xs shadow-sm"
           data-testid="attendance-info-strip">
        {["admin", "super_admin"].includes(effectiveRole) && (
          <>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-suite-neutral-500">Azienda</span>
              <Select value={companyId || ""} onValueChange={setCompanyId}>
                <SelectTrigger className="w-48 h-7 text-xs"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-px h-4 bg-suite-neutral-200" />
          </>
        )}
        {effectiveRole !== "employee" && (
          <>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-suite-neutral-500">Dipendente</span>
              <button type="button" className="h-7 w-7 grid place-items-center rounded-md border border-suite-neutral-200 bg-white disabled:opacity-30"
                aria-label="Dipendente precedente" data-testid="attendance-employee-select-previous"
                disabled={filterActiveInMonth(employees, year, month).filter((e) => !["admin", "super_admin"].includes(effectiveRole) || !companyId || e.company_id === companyId).findIndex((e) => e.id === employeeId) <= 0}
                onClick={() => {
                  const list = filterActiveInMonth(employees, year, month).filter((e) => !["admin", "super_admin"].includes(effectiveRole) || !companyId || e.company_id === companyId);
                  const index = list.findIndex((e) => e.id === employeeId);
                  if (index > 0) setEmployeeId(list[index - 1].id);
                }}><ChevronLeft size={15} /></button>
              <Select value={employeeId || ""} onValueChange={setEmployeeId}>
                <SelectTrigger className="w-56 h-7 text-xs" data-testid="attendance-employee-select">
                  <SelectValue placeholder="Seleziona" />
                </SelectTrigger>
                <SelectContent>
                  {/* v0.99.29 Fase 2: filtra dipendenti attivi nel mese selezionato.
                      v0.99.30-hotfix-p0 (FIX3): defense-in-depth — filtra anche per
                      company_id lato render, oltre al filtro backend. Previene
                      contaminazione visiva durante window di refetch dopo cambio azienda. */}
                  {(() => {
                    const list = filterActiveInMonth(employees, year, month)
                      .filter((e) => !["admin", "super_admin"].includes(effectiveRole) || !companyId || e.company_id === companyId);
                    if (list.length === 0) {
                      return (
                        <div
                          className="px-3 py-4 text-center text-xs text-suite-neutral-500 italic"
                          data-testid="attendance-no-employees"
                        >
                          Nessun dipendente presente
                        </div>
                      );
                    }
                    return list.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.last_name} {e.first_name}
                        {e.cessation_date && <span className="text-suite-neutral-400 ml-2 text-xs">· {statusLabel(e)}</span>}
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
              <button type="button" className="h-7 w-7 grid place-items-center rounded-md border border-suite-neutral-200 bg-white disabled:opacity-30"
                aria-label="Dipendente successivo" data-testid="attendance-employee-select-next"
                disabled={(() => { const list = filterActiveInMonth(employees, year, month).filter((e) => !["admin", "super_admin"].includes(effectiveRole) || !companyId || e.company_id === companyId); const index = list.findIndex((e) => e.id === employeeId); return index < 0 || index >= list.length - 1; })()}
                onClick={() => {
                  const list = filterActiveInMonth(employees, year, month).filter((e) => !["admin", "super_admin"].includes(effectiveRole) || !companyId || e.company_id === companyId);
                  const index = list.findIndex((e) => e.id === employeeId);
                  if (index >= 0 && index < list.length - 1) setEmployeeId(list[index + 1].id);
                }}><ChevronRight size={15} /></button>
            </div>
            <div className="w-px h-4 bg-suite-neutral-200" />
          </>
        )}
        {/* Frecce prev/next mese sono ora nell'header (riga 2). Rimosse dall'info-strip
            per non duplicare (v0.99.30-c2-close C2.3). */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest font-semibold text-suite-neutral-500">Stato</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-28 h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="draft">Bozza</SelectItem>
              <SelectItem value="sent">Inviato</SelectItem>
              <SelectItem value="approved">Approvato</SelectItem>
              <SelectItem value="rejected">Respinto</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* v0.99.30-c2-close (C2.3 final): controllo compatto mese/anno
            integrato nell'info-strip. Nessuna riga autonoma: sui viewport
            stretti può andare a capo (flex-wrap) mantenendo il pannello
            unico. Frecce prev/next coerenti in stile e dimensione (h-7 w-7). */}
        <SuiteMonthPicker
          year={year}
          month={month}
          size="sm"
          testid="attendance-month-picker"
          onChange={({ year: nextYear, month: nextMonth }) => {
            setYear(nextYear);
            setMonth(nextMonth);
          }}
        />

        {/* KPI inline (primari) — separati da spacer, allineati a destra */}
        <div className="w-px h-4 bg-suite-neutral-200 ml-auto" />
        <InlineKpi label="Teor." value={fmtHours(totals.theoretical)} testid="kpi-teor" />
        <InlineKpi label="Lavor." value={fmtHours(totals.worked)} testid="kpi-lavor" />
        <InlineKpi label="Saldo" value={fmtHoursSigned(balance)} tone={balance >= 0 ? "emerald" : "red"} testid="kpi-saldo" />
        <InlineKpi label="Progr." value={fmtHoursSigned(progressive)} tone={progressive >= 0 ? "sky" : "red"} testid="kpi-progr" />
        <div className="w-px h-4 bg-suite-neutral-200" />

        {/* Toggle Vista: segmented compatto "Griglia / Chip" */}
        <div className="inline-flex border border-suite-neutral-200 rounded-md overflow-hidden h-7" data-testid="attendance-view-toggle">
          <button
            type="button" onClick={() => setViewMode("compact")}
            data-testid="attendance-view-compact"
            title="Vista Griglia — tutte le causali in colonna"
            className={`px-2 text-[11px] font-semibold inline-flex items-center gap-1 transition-colors ${viewMode === "compact" ? "bg-suite-primary text-white" : "bg-white text-suite-neutral-600 hover:bg-suite-neutral-100"}`}
          >
            <TableProperties size={12} /> Griglia
          </button>
          <button
            type="button" onClick={() => setViewMode("dropdown")}
            data-testid="attendance-view-dropdown"
            title="Vista Chip — causali selezionabili dal menu"
            className={`px-2 text-[11px] font-semibold inline-flex items-center gap-1 transition-colors border-l border-suite-neutral-200 ${viewMode === "dropdown" ? "bg-suite-primary text-white" : "bg-white text-suite-neutral-600 hover:bg-suite-neutral-100"}`}
          >
            <Rows3 size={12} /> Chip
          </button>
        </div>

        {/* Icona hint centesimi (tooltip completo su hover) */}
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button"
                      data-testid="attendance-hint-centesimi"
                      className="h-7 w-7 rounded-md text-suite-primary/70 hover:bg-suite-primary-light hover:text-suite-primary transition-colors inline-flex items-center justify-center">
                <Info size={13} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="end" className="max-w-xs text-xs">
              {HINT_CENTESIMI}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* TOOLBAR AZIONI (v1.5.6): gerarchia visiva SX=strumenti / DX=workflow.
          - SX: Compila dal teorico · Inserisci periodo · Altre operazioni
          - DX: Salva · [Badge Bozza + Concludi cartellino] · Concludi mese azienda
                · Approva tutto (sent_to_company) · Invia per approvazione (employee)
                · Passa ad Assenze
          Nessun separatore verticale: separazione affidata a `justify-between`. */}
      <div className="flex flex-wrap items-center gap-1.5 justify-between">
        {/* ═════ GRUPPO SINISTRA · Strumenti di lavoro sul cartellino ═════ */}
        <div className="flex flex-wrap items-center gap-1.5" data-testid="attendance-toolbar-tools">
        {isExternalConsultant && (
          <div className="rounded-md border border-suite-neutral-200 bg-suite-neutral-50 px-3 py-1.5 text-xs font-semibold text-suite-neutral-600">
            Consultazione in sola lettura
          </div>
        )}
        {!isExternalConsultant && <>
        {/* C3-B.2.8: Salva bozze visibile per employee (compilazione del proprio
            cartellino) e per admin/client. Il gate operativo è `monthEditable`
            che disabilita in stato sent/approved/closed. */}
        {/* v1.2.1: "Completa ore ordinarie" disponibile anche al dipendente,
            usa lo STESSO helper (motore centrale) di admin/consulente. */}
        <SuiteButton
          size="sm"
          variant="secondary"
          icon={Sparkles}
          onClick={prefillFromTheoretical}
          disabled={!employeeId || !monthEditable}
          data-testid="attendance-fill-theoretical-btn"
        >
          {effectiveRole === "employee" ? "Completa ore ordinarie" : "Compila dal teorico"}
        </SuiteButton>
        <SuiteButton
          size="sm"
          variant="secondary"
          icon={Calendar}
          onClick={openRangeDialog}
          disabled={!employeeId || !monthEditable}
          data-testid="attendance-range-btn"
        >
          Inserisci periodo
        </SuiteButton>
        {effectiveRole !== "employee" && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SuiteButton size="sm" variant="secondary" icon={EllipsisVertical}
                aria-label="Altre operazioni" data-testid="attendance-more-actions-btn">
                Altre operazioni
              </SuiteButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64" data-testid="attendance-more-actions-menu">
              {/* v1.5.4 · Azioni Foundation ActionCatalog integrate in "Altre operazioni" */}
              {(() => {
                const actions = buildAttendanceActions({
                  employeeId, companyId: currentEmp?.company_id || companyId,
                  year, month, rows, monthData, setRows, dirtyRef,
                  helpers: {
                    computeTheoreticalPrefill: _computeTheoreticalPrefill,
                    fetchMonthHolidays: _fetchMonthHolidays,
                    formatHoursIt: _formatHoursIt,
                  },
                });
                if (actions.length === 0) return null;
                return (
                  <>
                    {actions.map((a) => {
                      const Icon = a.icon;
                      return (
                        <DropdownMenuItem
                          key={a.id}
                          disabled={!employeeId || !monthEditable}
                          onSelect={async () => {
                            try {
                              const res = await a.onExecute();
                              const msg = res?.message || `${a.title} completata`;
                              (toast[res?.kind || "info"] || toast.info)(msg);
                            } catch (e) {
                              toast.error(`Errore: ${e?.message || "operazione non riuscita"}`);
                            }
                          }}
                          data-testid={`attendance-action-${a.id}`}
                        >
                          {Icon && <Icon />} {a.title}
                        </DropdownMenuItem>
                      );
                    })}
                    <DropdownMenuSeparator />
                  </>
                );
              })()}
              <DropdownMenuItem onSelect={openCessationDialog} disabled={!employeeId || !monthEditable}
                data-testid="attendance-cessation-btn">
                <UserMinus /> Cessazione rapporto
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={clearWholeMonth} disabled={!employeeId || !monthEditable}
                className="text-suite-error focus:text-suite-error" data-testid="attendance-clear-month-btn">
                <XCircle /> Svuota mese
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => bulkStatus("rejected")} disabled={!employeeId || !monthEditable}
                className="text-suite-error focus:text-suite-error" data-testid="attendance-reject-btn">
                <XCircle /> Respingi tutto
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        </>}
        </div>
        {/* ═════ GRUPPO DESTRA · Azioni di workflow ═════ */}
        <div className="flex flex-wrap items-center gap-1.5" data-testid="attendance-toolbar-workflow">
        {/* M-WF-01 · metodo alternativo di inserimento: alimenta questo stesso cartellino */}
        {employeeId && !isEmployeeRole && (
          <Link to={`/presenze/compila-orari?employee=${employeeId}&year=${year}&month=${month}`} data-testid="attendance-open-timesheet-link">
            <SuiteButton size="md" icon={Clock3} data-testid="attendance-open-timesheet-btn">Compila Orari</SuiteButton>
          </Link>
        )}
        {!isExternalConsultant && <>
        {/* Salva — verde primario, azione operativa principale. */}
        <SuiteButton
          size="md" icon={Save} variant="primary"
          onClick={() => saveAll()}
          loading={saving}
          disabled={saving || !employeeId || !monthEditable}
          data-testid="attendance-save-btn"
          className="!bg-suite-primary !text-white !border-suite-primary hover:!bg-suite-primary/90 shadow-sm font-semibold"
        >
          Salva
        </SuiteButton>
        {/* MonthWorkflowControl renderizza [Badge Bozza + Concludi cartellino].
            Concludi mese azienda e' subito adiacente per creare la famiglia
            "operazioni di chiusura workflow" (singolo dipendente | intera azienda). */}
        {employeeId && (
          <MonthWorkflowControl
            companyId={currentEmp?.company_id || companyId}
            employeeId={employeeId}
            year={year} month={month}
            refreshKey={workflowRefresh}
            onStateChange={handleWorkflowStateChange}
            hasUnsavedChanges={() => dirtyRef.current}
            beforeClose={saveBeforeClose}
            afterClose={load}
          />
        )}
        {/* Concludi mese azienda — blu antracite con icona Calendar+Lock per
            enfatizzare l'operazione MASSIVA sull'azienda (contro il "Concludi
            cartellino" verde outline che agisce sul singolo dipendente). */}
        {["admin", "super_admin", "studio_operator", "client", "company_admin", "company_responsible"].includes(effectiveRole) && (
          <SuiteButton
            size="sm"
            variant="secondary"
            icon={CalendarCheck2}
            onClick={() => openBulkCloseDialog()}
            data-testid="attendance-bulk-close-btn"
            className="!bg-suite-neutral-800 !text-white !border-suite-neutral-800 hover:!bg-suite-neutral-700"
          >
            Concludi mese azienda
          </SuiteButton>
        )}
        {/* v1.5.5 · "Approva tutto" — visibile SOLO quando esiste un vero flusso
            di approvazione da chiudere (workflowState === sent_to_company). */}
        {effectiveRole !== "employee" && workflowState === "sent_to_company" && (
        <SuiteButton
          size="sm"
          variant="secondary"
          icon={CheckCircle2}
          onClick={approveMonthWorkflow}
          disabled={!employeeId}
          className="!text-suite-accent-hover hover:!border-suite-accent/40"
          data-testid="attendance-approve-btn"
        >
          Approva tutto
        </SuiteButton>)}
        {isEmployeeRole && <SuiteButton
          size="sm" variant="secondary" icon={Send}
          onClick={async () => {
            // v0.99.30-c2-close (C2.4): pre-check quota sabati mensile prima
            // dell'invio. Nessuna modifica automatica; solo warning informativo.
            // C3-B.2.8: il dipendente NON è tenuto a gestire la quota sabati
            // (che è compito dell'operatore studio/azienda). Saltiamo il pre-check
            // per evitare 403 rumorosi su /hour-profiles.
            if (!isEmployeeRole) { try {
              const profRes = await api.get("/hour-profiles", { params: { employee_id: employeeId } });
              const profiles = profRes.data || [];
              const monthFirst = `${year}-${String(month).padStart(2, "0")}-01`;
              const applicable = profiles
                .filter((p) => (p.effective_from || "0000-01-01") <= monthFirst)
                .sort((a, b) => (b.effective_from || "").localeCompare(a.effective_from || ""))[0];
              const Q = Number(applicable?.saturday_quota_days_per_month || 0);
              if (Q > 0) {
                const excRes = await api.get(
                  `/employees/${employeeId}/schedule-exceptions`,
                  { params: { year } }
                );
                const monthPrefix = `${year}-${String(month).padStart(2, "0")}-`;
                const assigned = (excRes.data || []).filter((e) => {
                  if (!e.date?.startsWith(monthPrefix)) return false;
                  const wd = new Date(e.date + "T00:00:00").getDay(); // 6=Sat in JS
                  return wd === 6 && Number(e.hours || 0) > 0;
                }).length;
                if (assigned < Q) {
                  const ok = window.confirm(
                    `Attenzione — quota sabati mensile non completa.\n` +
                    `Previsti: ${Q}\nAssegnati: ${assigned}\n\n` +
                    `Confermi comunque l'invio per approvazione?\n` +
                    `Annulla per completare la selezione.`
                  );
                  if (!ok) return;
                }
              }
            } catch (_e) { /* fallback: procedi normalmente */ }
            } // chiude if (!isEmployeeRole)
            await saveAll("sent");
            // C3-B.2.8 · Se il chiamante è un dipendente, dopo il salvataggio
            // deve scattare la transizione di workflow "send" (draft/to_correct
            // → sent_to_company). Il pulsante "Invia per approvazione" è la
            // singola azione visibile all'operatore-dipendente.
            if (isEmployeeRole && (workflowState === "draft" || workflowState === "to_correct")) {
              try {
                const cid = currentEmp?.company_id || companyId;
                const st = await api.get("/workflow/months/state", {
                  params: { company_id: cid, employee_id: employeeId, year, month },
                });
                const wf = st.data;
                await api.post("/workflow/months/transition", {
                  workflow_id: wf.id,
                  action: "send",
                  expected_version: wf.version,
                });
                setWorkflowState("sent_to_company");
                toast.success("Mese inviato per approvazione");
              } catch (e) { toast.error(apiErr(e)); }
            }
          }}
          disabled={saving || !employeeId}
          data-testid="attendance-send-btn"
        >
          {workflowState === "to_correct" ? "Reinvia" : "Invia per approvazione"}
        </SuiteButton>}
        <SuiteButton
          as={Link} to="/guidata" size="sm" variant="ghost"
          icon={ArrowLeftRight} iconRight={Wand2}
          data-testid="switch-to-guided"
        >
          Passa ad Assenze e Permessi
        </SuiteButton>
        </>}
        </div>
      </div>

        {/* La nota appartiene alla barra operativa: includerla nel blocco
            misurato impedisce all'intestazione della griglia di sovrapporsi. */}
        <div className="text-[11px] text-suite-neutral-400 pl-1 hidden lg:block" data-testid="attendance-f8-hint">
          Ctrl+F2: compila le giornate vuote del mese. F9: copia le ore teoriche nella cella causale selezionata. Festività e Santo Patrono vengono riconosciuti automaticamente.
        </div>
        </div>{/* /hidden md:block */}
      </div>{/* /attendance-sticky-workbar */}

      {/* ===================== v1.1.1 · UX Mobile ===================== */}
      {isCompact && (<>
      {/* Sheet Dettagli e strumenti (mobile) */}
      <Sheet open={mobileDetailsOpen} onOpenChange={setMobileDetailsOpen}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto lg:hidden"
                      data-testid="attendance-mobile-details-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="text-base">Dettagli e strumenti</SheetTitle>
            <SheetDescription className="sr-only">Selezione mese, azienda e strumenti secondari</SheetDescription>
          </SheetHeader>
          <div className="mt-3 space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-suite-neutral-500 mb-1">Mese</div>
              <SuiteMonthPicker year={year} month={month}
                onChange={(y, m) => { setYear(y); setMonth(m); }} />
            </div>
            {["admin", "super_admin"].includes(effectiveRole) && companies.length > 1 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-suite-neutral-500 mb-1">Azienda</div>
                <Select value={companyId || ""} onValueChange={(v) => { setCompanyId(v); }}>
                  <SelectTrigger className="w-full h-9"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!isExternalConsultant && (
              <div className="grid grid-cols-1 gap-2 pt-1">
                <SuiteButton size="sm" variant="secondary" icon={Sparkles}
                  onClick={() => { setMobileDetailsOpen(false); prefillFromTheoretical(); }}
                  disabled={!employeeId || !monthEditable}
                  data-testid="mobile-fill-theoretical-btn">
                  Compila dal teorico
                </SuiteButton>
                <SuiteButton size="sm" variant="secondary" icon={Calendar}
                  onClick={() => { setMobileDetailsOpen(false); openRangeDialog(); }}
                  disabled={!employeeId || !monthEditable}
                  data-testid="mobile-range-btn">
                  Inserisci periodo
                </SuiteButton>
                {effectiveRole !== "employee" && workflowState === "sent_to_company" && (
                  <SuiteButton size="sm" variant="secondary" icon={CheckCircle2}
                    onClick={() => { setMobileDetailsOpen(false); approveMonthWorkflow(); }}
                    disabled={!employeeId}
                    data-testid="mobile-approve-btn">
                    Approva tutto
                  </SuiteButton>
                )}
              </div>
            )}
            {employeeId && !isExternalConsultant && (
              <div className="pt-2 border-t border-suite-neutral-200">
                <div className="text-[10px] uppercase tracking-widest text-suite-neutral-500 mb-2">Workflow mese</div>
                <MonthWorkflowControl
                  companyId={currentEmp?.company_id || companyId}
                  employeeId={employeeId}
                  year={year} month={month}
                  refreshKey={workflowRefresh}
                  onStateChange={handleWorkflowStateChange}
                  hasUnsavedChanges={() => dirtyRef.current}
                  beforeClose={saveBeforeClose}
                  afterClose={load}
                />
              </div>
            )}
            <div className="text-[11px] text-suite-neutral-400 pt-2 border-t border-suite-neutral-200">
              Suggerimento: nella griglia scorri in orizzontale per vedere tutte le colonne. La prima colonna (Data) resta fissa.
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Sheet Dipendente (mobile picker) */}
      <Sheet open={mobileEmpPickerOpen} onOpenChange={setMobileEmpPickerOpen}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto lg:hidden"
                      data-testid="attendance-mobile-emp-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="text-base">Seleziona dipendente</SheetTitle>
            <SheetDescription className="sr-only">Elenco dipendenti attivi nel mese</SheetDescription>
          </SheetHeader>
          <div className="mt-2 divide-y divide-suite-neutral-100">
            {(() => {
              const list = filterActiveInMonth(employees, year, month)
                .filter((e) => !["admin", "super_admin"].includes(effectiveRole) || !companyId || e.company_id === companyId);
              if (list.length === 0) {
                return <div className="py-6 text-center text-sm text-suite-neutral-500">Nessun dipendente disponibile</div>;
              }
              return list.map((e) => (
                <button key={e.id} type="button"
                  onClick={() => { setEmployeeId(e.id); setMobileEmpPickerOpen(false); }}
                  className={`w-full text-left px-2 py-3 flex items-center justify-between ${employeeId === e.id ? "bg-suite-primary-light/60 text-suite-primary font-semibold" : ""}`}
                  data-testid={`mobile-emp-option-${e.id}`}>
                  <span>{e.last_name} {e.first_name}</span>
                  {employeeId === e.id && <CheckCircle2 size={16} className="text-suite-primary" />}
                </button>
              ));
            })()}
          </div>
        </SheetContent>
      </Sheet>
      </>)}
      {/* ===================== /v1.1.1 · UX Mobile ===================== */}

      <Dialog open={bulkCloseOpen} onOpenChange={setBulkCloseOpen}>
        <DialogContent data-testid="attendance-bulk-close-dialog">
          <DialogHeader>
            <DialogTitle>Concludi mese azienda</DialogTitle>
            <DialogDescription>
              Verranno concluse {bulkCloseCount.eligible} lavorazioni ancora aperte per {MONTHS_IT[month - 1]} {year}.
              {bulkCloseCount.already_closed > 0 && ` (${bulkCloseCount.already_closed} già concluse resteranno invariate).`}
              <br />Il singolo cartellino potrà comunque essere riaperto successivamente dal Workflow di ciascun dipendente.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <SuiteButton size="sm" variant="secondary"
              onClick={() => setBulkCloseOpen(false)}
              disabled={bulkClosing}
              data-testid="attendance-bulk-close-cancel">Annulla</SuiteButton>
            <SuiteButton size="sm" variant="primary" icon={Lock}
              onClick={confirmBulkClose}
              loading={bulkClosing}
              disabled={bulkClosing || bulkCloseCount.eligible === 0}
              className="!bg-suite-primary !text-white !border-suite-primary"
              data-testid="attendance-bulk-close-confirm">
              Conferma chiusura
            </SuiteButton>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={settlementSplitOpen} onOpenChange={setSettlementSplitOpen}>
        <DialogContent formDialog className="max-w-md" data-testid="bank-hours-split-dialog">
          <DialogHeader>
            <DialogTitle>Ripartisci recupero banca ore</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border border-suite-neutral-200 bg-suite-neutral-50 px-3 py-2 text-sm">
              Residuo da recuperare: <b>{fmtHours(Math.abs(Number(settlementContext?.residual_before_settlement || 0)))} h</b>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <DurationLabel data-testid="bank-hours-split-exf">Da Ex festivita</DurationLabel>
                <Input
                  inputMode="decimal"
                  value={settlementSplit.exf}
                  onChange={(e) => setSettlementSplit((v) => ({ ...v, exf: e.target.value }))}
                  data-testid="bank-hours-split-exf"
                />
                <p className="mt-1 text-[11px] text-suite-neutral-500">
                  Disponibili: {fmtHours(settlementContext?.exf_available || 0)} h
                </p>
              </div>
              <div>
                <DurationLabel data-testid="bank-hours-split-rol">Da ROL / Permessi</DurationLabel>
                <Input
                  inputMode="decimal"
                  value={settlementSplit.rol}
                  onChange={(e) => setSettlementSplit((v) => ({ ...v, rol: e.target.value }))}
                  data-testid="bank-hours-split-rol"
                />
                <p className="mt-1 text-[11px] text-suite-neutral-500">
                  Disponibili: {fmtHours(settlementContext?.rol_available || 0)} h
                </p>
              </div>
            </div>
            <p className="text-xs text-suite-neutral-500">
              Le due quote devono coprire esattamente il residuo. Il sistema propone di utilizzare prima le Ex festivita disponibili e poi i ROL.
            </p>
          </div>
          <DialogFooter>
            <SuiteButton variant="secondary" onClick={() => setSettlementSplitOpen(false)}>Annulla</SuiteButton>
            <SuiteButton onClick={saveMixedSettlement} loading={settlementSaving} disabled={settlementSaving}>
              Registra e azzera
            </SuiteButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rangeOpen} onOpenChange={setRangeOpen}>
        <DialogContent formDialog className="max-w-md">
          <DialogHeader>
            <DialogTitle>Inserisci causale per periodo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-suite-neutral-500">
              Dipendente: <b className="text-suite-neutral-800">{empDisplay}</b>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Dal</Label>
                <Input
                  type="date"
                  value={rangeData.start_date}
                  onChange={(e) => {
                    const value = e.target.value;
                    setRangeData((prev) => ({
                      ...prev,
                      start_date: value,
                      end_date: prev.end_date < value ? value : prev.end_date,
                    }));
                  }}
                  data-testid="attendance-range-from"
                />
              </div>
              <div>
                <Label className="text-xs">Al</Label>
                <Input
                  type="date"
                  min={rangeData.start_date}
                  value={rangeData.end_date}
                  onChange={(e) => setRangeData({ ...rangeData, end_date: e.target.value })}
                  data-testid="attendance-range-to"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Causale</Label>
              <Select
                value={rangeData.cause}
                onValueChange={(code) => {
                  const cause = operationalCauses.find((row) => row.code === code);
                  setRangeData({
                    ...rangeData,
                    cause: code,
                    range_day_mode: causeRangeDayMode(cause),
                  });
                }}
              >
                <SelectTrigger data-testid="attendance-range-cause"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {operationalCauses.map((cause) => (
                    <SelectItem key={cause.code} value={cause.code}>
                      {cause.label || CAUSE_LABELS[cause.code] || cause.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-[11px] text-suite-neutral-500 rounded-md bg-suite-neutral-50 border border-suite-neutral-200 px-2 py-1.5">
              Regola applicata:{" "}
              <b className="text-suite-neutral-700">
                {rangeData.range_day_mode === "calendar_days"
                  ? "tutti i giorni consecutivi"
                  : rangeData.range_day_mode === "exclude_sundays"
                    ? "tutti i giorni tranne la domenica"
                    : "solo giorni lavorativi"}
              </b>.
              {" "}Le ore derivano dal profilo; i riposi inclusi restano a zero ore.
            </div>
            <div>
              <Label className="text-xs">Note (opzionale)</Label>
              <Textarea
                rows={2}
                value={rangeData.notes}
                onChange={(e) => setRangeData({ ...rangeData, notes: e.target.value })}
                data-testid="attendance-range-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <SuiteButton variant="secondary" onClick={() => setRangeOpen(false)}>Annulla</SuiteButton>
            <SuiteButton onClick={saveRange} loading={rangeSaving} disabled={rangeSaving}>
              Registra periodo
            </SuiteButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cessationOpen} onOpenChange={setCessationOpen}>
        <DialogContent formDialog className="max-w-md" data-testid="cessation-dialog">
          <DialogHeader>
            <DialogTitle>Cessazione del rapporto</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Le giornate successive saranno escluse dai calcoli, senza
              cancellare lo storico. La cessazione diventerà definitiva
              nell'anagrafica solo con <b>Concludi cartellino</b>.
            </div>
            <div>
              <Label className="text-xs">Ultimo giorno di rapporto</Label>
              <Input
                type="date"
                min={`${year}-${String(month).padStart(2, "0")}-01`}
                max={`${year}-${String(month).padStart(2, "0")}-${new Date(year, month, 0).getDate()}`}
                value={cessationData.cessation_date}
                onChange={(e) => setCessationData({
                  ...cessationData, cessation_date: e.target.value,
                })}
                data-testid="cessation-date"
              />
            </div>
            <div>
              <Label className="text-xs">Motivo</Label>
              <Select
                value={cessationData.cessation_reason}
                onValueChange={(value) => setCessationData({
                  ...cessationData, cessation_reason: value,
                })}
              >
                <SelectTrigger data-testid="cessation-reason">
                  <SelectValue placeholder="Seleziona il motivo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DIMISSIONI">Dimissioni</SelectItem>
                  <SelectItem value="LICENZIAMENTO">Licenziamento</SelectItem>
                  <SelectItem value="PENSIONAMENTO">Pensionamento</SelectItem>
                  <SelectItem value="FINE_CONTRATTO">Fine contratto</SelectItem>
                  <SelectItem value="ALTRO">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Nota (opzionale)</Label>
              <Textarea
                rows={2}
                value={cessationData.note}
                onChange={(e) => setCessationData({
                  ...cessationData, note: e.target.value,
                })}
                data-testid="cessation-note"
              />
            </div>
          </div>
          <DialogFooter>
            <SuiteButton variant="secondary" onClick={() => setCessationOpen(false)}>
              Annulla
            </SuiteButton>
            <SuiteButton
              icon={UserMinus}
              onClick={savePendingCessation}
              loading={cessationSaving}
              disabled={cessationSaving}
              data-testid="cessation-confirm"
            >
              Predisponi cessazione
            </SuiteButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GRIGLIA */}
      {workflowDoc?.pending_cessation_date && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950"
          data-testid="cessation-pending-notice"
        >
          <UserMinus size={15} className="shrink-0 text-amber-700" />
          <span>
            <b>Cessazione predisposta:</b>{" "}
            ultimo giorno {formatDateIT(workflowDoc.pending_cessation_date)}
            {" · "}
            {workflowDoc.pending_cessation_reason?.replaceAll("_", " ")}.
            Sarà registrata in anagrafica con <b>Concludi cartellino</b>.
          </span>
          <SuiteButton
            size="sm"
            variant="ghost"
            onClick={clearPendingCessation}
            disabled={cessationSaving}
            className="ml-auto"
          >
            Annulla predisposizione
          </SuiteButton>
        </div>
      )}
      {monthData?.saturday_schedule && (
        <div
          className="flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900"
          data-testid="saturday-schedule-notice"
        >
          <Calendar size={15} className="shrink-0 text-sky-700" />
          <span>
            <b>Sabato lavorativo variabile:</b>{" "}
            {monthData.saturday_schedule.days_per_month}{" "}
            {monthData.saturday_schedule.days_per_month === 1 ? "sabato" : "sabati"} al mese
            {" · "}
            <b>{fmtHours(monthData.saturday_schedule.hours_per_day)} ore</b> per sabato.
          </span>
        </div>
      )}
      {monthData?.alternation_schedule?.active && (
        <div
          className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900"
          data-testid="alternation-schedule-notice"
        >
          <Calendar size={15} className="shrink-0 text-indigo-700" />
          <span>
            <b>Profilo settimanale alternato attivo.</b>{" "}
            Per questo mese puoi correggere le ore nella colonna <b>TEOR.</b>{" "}
            di qualsiasi giornata. La correzione resta su quella data e non
            modifica l&apos;alternanza del profilo futuro.
          </span>
        </div>
      )}
      {!monthEditable && (
        <div
          className="text-xs px-3 py-2 rounded border border-suite-warning bg-suite-warning/10 text-suite-warning-hover flex items-center gap-2"
          data-testid="mw-readonly-banner"
        >
          <Lock size={14} />
          {isEmployeeRole
            ? `Cartellino in stato «${workflowState}» — sola lettura. Attendere richiesta di correzione per modificare.`
            : `Mese in stato «${workflowState}» — sola lettura. Riportare in Bozza / Da correggere per modificare.`}
        </div>
      )}
      <fieldset disabled={!monthEditable} data-testid="attendance-fieldset" className="contents">
      <SuiteCard padding="none" testid="attendance-grid-card">
        <TooltipProvider delayDuration={150}>
        {viewMode === "compact" ? (
          <Table
            className="lg:min-w-[980px] xl:min-w-[1180px] 3xl:min-w-[1440px]"
            wrapperClassName="!overflow-visible"
          >
            <TableHeader
              className="bg-white shadow-[0_1px_0_rgba(0,0,0,0.06)]"
              data-testid="attendance-cause-header"
            >
              <TableRow>
                <TableHead style={{ top: stickyWorkbarHeight }} className="text-xs uppercase tracking-widest bg-white py-1.5 px-2 sticky left-0 z-30 shadow-[1px_0_0_rgba(0,0,0,0.06)]"><span className="inline-flex items-center gap-1.5">Data <DurationFormatBadge data-testid="attendance-grid-format-badge" /></span></TableHead>
                <TableHead style={{ top: stickyWorkbarHeight }} className="sticky z-20 text-xs uppercase tracking-widest text-center bg-white py-1.5 px-1">
                  <Tooltip>
                    <TooltipTrigger asChild><span className="cursor-help">Teor.</span></TooltipTrigger>
                    <TooltipContent>Ore teoriche (da profilo orario)</TooltipContent>
                  </Tooltip>
                </TableHead>
                {visibleCauseCodes.map((c) => (
                  <TableHead key={c} style={{ top: stickyWorkbarHeight }} className="sticky z-20 text-[9px] xl:text-[10px] uppercase tracking-widest text-center bg-white py-1.5 px-0.5 xl:px-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={`px-0.5 xl:px-1 py-0.5 rounded cursor-help ${CAUSE_COLORS[c].bg} ${CAUSE_COLORS[c].text} border ${CAUSE_COLORS[c].border}`}>
                          {CAUSE_SHORT[c]}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent><span className="font-semibold">{CAUSE_SHORT[c]}</span> = {CAUSE_LABELS[c]}</TooltipContent>
                    </Tooltip>
                  </TableHead>
                ))}
                <TableHead style={{ top: stickyWorkbarHeight }} className="sticky z-20 bg-white py-1.5 px-0.5 text-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button type="button" className="h-6 w-6 rounded hover:bg-suite-neutral-100 inline-flex items-center justify-center" title="Altre causali e conguaglio banca ore" data-testid="attendance-cause-menu-btn">
                        <EllipsisVertical size={14} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72 max-h-[420px] overflow-y-auto">
                      {settlementContext && (
                        <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-suite-neutral-500 font-semibold">
                          {settlementContext.closure_reason === "employment_cessation"
                            ? "Conguaglio banca ore · cessazione"
                            : "Conguaglio banca ore"}
                        </div>
                      )}
                      {settlementContext && !settlementContext.settlement && Number(settlementContext.residual_before_settlement) > 0 && (
                        <DropdownMenuItem disabled={settlementSaving} onSelect={() => saveSemesterSettlement("STR_BO")}>
                          <b className="w-16">STR.BO</b> Liquida {fmtHours(settlementContext.residual_before_settlement)} h
                        </DropdownMenuItem>
                      )}
                      {settlementContext && !settlementContext.settlement && Number(settlementContext.residual_before_settlement) < 0 && <>
                        <DropdownMenuItem
                          disabled={settlementSaving}
                          onSelect={openMixedSettlement}
                        >
                          <b className="w-16">MISTO</b> Ripartisci tra Ex Fest. e ROL
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled={settlementSaving} onSelect={() => saveSemesterSettlement("ROL_BO")}>
                          <b className="w-16">ROL.BO</b> Recupera da ROL ({fmtHours(settlementContext.rol_available)} h)
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled={settlementSaving} onSelect={() => saveSemesterSettlement("EXF_BO")}>
                          <b className="w-16">EXF.BO</b> Recupera da Ex Fest. ({fmtHours(settlementContext.exf_available)} h)
                        </DropdownMenuItem>
                      </>}
                      {settlementContext?.settlement && (
                        <DropdownMenuItem disabled={settlementSaving} className="text-suite-error focus:text-suite-error" onSelect={reverseSemesterSettlement}>
                          Annulla {settlementContext.settlement.settlement_type} · {fmtHours(settlementContext.settlement.amount)} h
                        </DropdownMenuItem>
                      )}
                      {settlementContext && <DropdownMenuSeparator />}
                      <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-suite-neutral-500 font-semibold">Altre causali</div>
                      {optionalCauseChoices.map((code) => (
                        <DropdownMenuItem key={code} onSelect={() => toggleOptionalCause(code)}>
                          <span className="w-5">{optionalCauseCodes.includes(code) ? "✓" : ""}</span>
                          <b className="w-14">{CAUSE_SHORT[code]}</b> {CAUSE_LABELS[code]}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableHead>
                <TableHead style={{ top: stickyWorkbarHeight }} className="sticky z-20 text-xs uppercase tracking-widest text-right whitespace-nowrap bg-white py-1.5 px-1">Saldo</TableHead>
                <TableHead style={{ top: stickyWorkbarHeight }} className="sticky z-20 text-xs uppercase tracking-widest lg:min-w-[60px] xl:min-w-[80px] bg-white py-1.5 px-2">Note</TableHead>
                <TableHead style={{ top: stickyWorkbarHeight }} className="text-[10px] uppercase tracking-widest whitespace-nowrap w-[62px] xl:w-[72px] bg-white py-1.5 px-1 sticky right-0 z-30 shadow-[-1px_0_0_rgba(0,0,0,0.06)]" data-testid="col-stato-header">Stato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDays.map((d) => {
                const row = rows[d.date] || { cause_hours: {}, notes: "", status: d.status };
                const isRest = d.theoretical === 0;
                const isScheduledSaturday =
                  d.weekday === 5 && d.is_variable_saturday && d.theoretical > 0;
                return (
                  <TableRow key={d.date}
                            className={`group hover:bg-suite-surface-hover ${
                              isScheduledSaturday
                                ? "bg-sky-50/80"
                                : (isRest ? "bg-suite-neutral-50/60" : "")
                            }`}
                            data-testid={`attendance-row-${d.date}`}>
                    <TableCell className="whitespace-nowrap px-2 py-1 sticky left-0 z-10 bg-white group-hover:bg-suite-surface-hover shadow-[1px_0_0_rgba(0,0,0,0.06)]">
                      <div className="flex items-center gap-1">
                        <div>
                          <div className={`text-sm font-semibold leading-tight ${isScheduledSaturday ? "text-sky-700" : ""}`}>{d.date.slice(8, 10)}/{d.date.slice(5, 7)}</div>
                          <div className={`text-[10px] leading-none ${
                            isScheduledSaturday
                              ? "text-sky-700 font-semibold"
                              : (isRest ? "text-suite-neutral-400 font-semibold" : "text-suite-neutral-500")
                          }`}>{WEEKDAYS_SHORT[d.weekday]}</div>
                        </div>
                        {/* v0.99.30-c2-close (C2.4): azione Imposta sabato lavorativo.
                            Compare solo sui sabati (weekday=5). Ruolo employee non ha l'azione. */}
                        {d.weekday === 5 && effectiveRole !== "employee" && (
                          <button
                            type="button"
                            onClick={() => isScheduledSaturday
                              ? removeSaturdayWorked(d.date)
                              : setSaturdayWorked(d.date)}
                            data-testid={`att-set-saturday-${d.date}`}
                            title={isScheduledSaturday
                              ? "Rimuovi questo sabato lavorativo"
                              : "Imposta o sposta qui il sabato lavorativo del mese"}
                            className={`transition-opacity h-6 w-6 rounded inline-flex items-center justify-center ${
                              isScheduledSaturday
                                ? "opacity-100 text-sky-700 bg-sky-100 hover:bg-sky-200"
                                : "opacity-0 group-hover:opacity-100 text-suite-primary hover:bg-suite-primary-light"
                            }`}
                          >
                            <Calendar size={12} />
                          </button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right p-0.5">
                      <Input
                        type="text"
                        inputMode="decimal"
                        pattern={durFmt === "hm" ? undefined : "[0-9]*[.,]?[0-9]{0,2}"}
                        value={row.theoretical_override !== undefined ? String(row.theoretical_override).replace(".", ",") : ""}
                        onChange={(e) => setTheoretical(d.date, e.target.value)}
                        onBlur={() => commitTheoretical(d.date)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitTheoretical(d.date);
                            focusNextInColumn(d.date, "att-teor-{date}");
                          } else if (focusGridCell(d.date, "__THEORETICAL__", e.key)) {
                            e.preventDefault();
                            commitTheoretical(d.date);
                          }
                        }}
                        aria-invalid={isDurationInvalid(row.theoretical_override, durFmt) || undefined}
                        className={`${isDurationInvalid(row.theoretical_override, durFmt) ? DUR_INVALID : ""} w-full lg:w-[52px] xl:w-[64px] mx-auto h-7 text-right font-mono !text-[10px] xl:!text-xs px-0.5 xl:px-1 ${
                          isScheduledSaturday
                            ? "text-sky-700 placeholder:text-sky-700 border-sky-300 bg-sky-50 font-semibold"
                            : (row.theoretical_override !== undefined
                              ? "text-suite-neutral-800 border-suite-accent-border bg-suite-accent-light/40"
                              : "text-suite-neutral-400")
                        }`}
                        placeholder={fmtHours(d.theoretical)}
                        title={row.theoretical_override !== undefined
                          ? `Override giornaliero. Profilo: ${fmtHours(d.theoretical)}h`
                          : `Da profilo: ${fmtHours(d.theoretical)}h — digita per sovrascrivere`}
                        data-testid={`att-teor-${d.date}`}
                      />
                    </TableCell>
                    {visibleCauseCodes.map((c) => {
                      // Checkpoint 2 . Ferie in giorni: se il record ha
                      // cause_units[c] (attualmente solo FERIE in "gg"),
                      // mostriamo un badge in sola lettura "1,00 gg" al
                      // posto dell'input in ore.
                      const daysMeta = row.cause_units?.[c];
                      const qtyDays = Number(daysMeta?.qty || 0);
                      if (qtyDays > 0 && daysMeta?.unit === "gg") {
                        return (
                          <TableCell key={c} className="p-0.5 text-center">
                            <div
                              className="mx-auto h-7 flex items-center justify-center rounded bg-amber-50 border border-amber-200 text-amber-900 font-mono !text-[10px] xl:!text-xs px-1 w-full lg:w-[44px] xl:w-[52px] 3xl:w-[60px]"
                              title={`${c} \u00b7 ${qtyDays.toFixed(2).replace('.', ',')} gg (registrato in giorni dal CCNL)`}
                              data-testid={`att-${d.date}-${c}-gg`}
                            >
                              {qtyDays.toFixed(2).replace('.', ',')}<span className="ml-0.5 opacity-70">gg</span>
                            </div>
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={c} className="p-0.5 text-center">
                          <Input
                            type="text"
                            inputMode="decimal"
                            pattern={durFmt === "hm" ? undefined : "[0-9]*[.,]?[0-9]{0,2}"}
                            value={row.cause_hours?.[c] !== undefined && row.cause_hours?.[c] !== "" ? String(row.cause_hours[c]).replace(".", ",") : ""}
                            onChange={(e) => setCause(d.date, c, e.target.value)}
                            onBlur={() => commitCause(d.date, c)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                commitCause(d.date, c);
                                focusNextInColumn(d.date, `att-{date}-${c}`);
                              } else if (focusGridCell(d.date, c, e.key)) {
                                e.preventDefault();
                                commitCause(d.date, c);
                              }
                            }}
                            aria-invalid={isDurationInvalid(row.cause_hours?.[c], durFmt) || undefined}
                            title={isDurationInvalid(row.cause_hours?.[c], durFmt) ? parseDuration(row.cause_hours[c], durFmt).error : undefined}
                            className={`no-spinner w-full lg:w-[34px] xl:w-[44px] 3xl:w-[52px] mx-auto h-7 text-right font-mono !text-[10px] xl:!text-xs px-0.5 xl:px-1 ${isDurationInvalid(row.cause_hours?.[c], durFmt) ? DUR_INVALID : ""}`}
                            placeholder="—"
                            data-testid={`att-${d.date}-${c}`}
                            autoComplete="off"
                          />
                        </TableCell>
                      );
                    })}
                    <TableCell className="p-0.5" aria-hidden="true" />
                    <TableCell className={`text-right font-mono font-semibold whitespace-nowrap px-1 text-xs ${d.daily_balance >= 0 ? "text-suite-accent-hover" : "text-suite-error"}`}>
                      {fmtHoursSigned(d.daily_balance)}
                    </TableCell>
                    <TableCell className="p-0.5 lg:min-w-[60px] xl:min-w-[80px]">
                      <Textarea
                        value={row.notes || ""}
                        onChange={(e) => setNotes(d.date, e.target.value)}
                        rows={1}
                        className="min-h-[28px] max-h-24 text-[10px] xl:text-xs py-0.5 px-1.5 w-full resize-y leading-tight"
                        placeholder="—"
                        data-testid={`att-notes-${d.date}`}
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap px-1 sticky right-0 z-10 bg-white group-hover:bg-suite-surface-hover shadow-[-1px_0_0_rgba(0,0,0,0.06)]">
                      <StatusBadge s={row.status || d.status} />
                    </TableCell>
                  </TableRow>
                );
              })}
              {(!monthData || filteredDays.length === 0) && (
                <TableRow>
                  <TableCell colSpan={visibleCauseCodes.length + 6} className="text-center text-suite-neutral-400 py-8">
                    <ClipboardList size={28} className="mx-auto mb-2 opacity-50" />
                    {monthLoading
                      ? "Caricamento del mese selezionato…"
                      : "Nessun dato — seleziona dipendente e mese"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        ) : (
          /* ============ VISTA CHIP ============ */
          <Table wrapperClassName="!overflow-visible">
            <TableHeader
              className="bg-white shadow-[0_1px_0_rgba(0,0,0,0.06)]"
              data-testid="attendance-chip-header"
            >
              <TableRow>
                <TableHead style={{ top: stickyWorkbarHeight }} className="sticky z-20 text-xs uppercase tracking-widest bg-white py-1.5 px-3 w-[110px]"><span className="inline-flex items-center gap-1.5">Data <DurationFormatBadge data-testid="attendance-chip-format-badge" /></span></TableHead>
                <TableHead style={{ top: stickyWorkbarHeight }} className="sticky z-20 text-xs uppercase tracking-widest text-right bg-white py-1.5 px-2 w-[90px]">
                  <Tooltip>
                    <TooltipTrigger asChild><span className="cursor-help">Teor.</span></TooltipTrigger>
                    <TooltipContent>Ore teoriche (da profilo orario)</TooltipContent>
                  </Tooltip>
                </TableHead>
                <TableHead style={{ top: stickyWorkbarHeight }} className="sticky z-20 text-xs uppercase tracking-widest bg-white py-1.5 px-3">Causali</TableHead>
                <TableHead style={{ top: stickyWorkbarHeight }} className="sticky z-20 text-xs uppercase tracking-widest text-right whitespace-nowrap bg-white py-1.5 px-2 w-[80px]">Saldo</TableHead>
                <TableHead style={{ top: stickyWorkbarHeight }} className="sticky z-20 text-xs uppercase tracking-widest bg-white py-1.5 px-2 w-[200px]">Note</TableHead>
                <TableHead style={{ top: stickyWorkbarHeight }} className="sticky z-20 text-xs uppercase tracking-widest whitespace-nowrap bg-white py-1.5 px-2 w-[100px]">Stato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDays.map((d) => {
                const row = rows[d.date] || { cause_hours: {}, notes: "", status: d.status };
                const isRest = d.theoretical === 0;
                const isScheduledSaturday =
                  d.weekday === 5 && d.is_variable_saturday && d.theoretical > 0;
                return (
                  <TableRow key={d.date}
                            className={`hover:bg-suite-surface-hover ${
                              isScheduledSaturday
                                ? "bg-sky-50/80"
                                : (isRest ? "bg-suite-neutral-50/60" : "")
                            }`}
                            data-testid={`attendance-row-${d.date}`}>
                    <TableCell className="whitespace-nowrap px-3 py-2 align-top">
                      <div className="flex items-center gap-1">
                        <div>
                          <div className={`text-sm font-semibold leading-tight ${isScheduledSaturday ? "text-sky-700" : "text-suite-neutral-800"}`}>{d.date.slice(8, 10)}/{d.date.slice(5, 7)}</div>
                          <div className={`text-[10px] leading-none mt-0.5 ${
                            isScheduledSaturday
                              ? "text-sky-700 font-semibold"
                              : (isRest ? "text-suite-neutral-400 font-semibold" : "text-suite-neutral-500")
                          }`}>{WEEKDAYS_SHORT[d.weekday]}</div>
                        </div>
                        {/* v0.99.30-c2-close (C2.4) — azione sabato lavorativo, vista Chip */}
                        {d.weekday === 5 && effectiveRole !== "employee" && (
                          <button
                            type="button"
                            onClick={() => isScheduledSaturday
                              ? removeSaturdayWorked(d.date)
                              : setSaturdayWorked(d.date)}
                            data-testid={`att-set-saturday-chip-${d.date}`}
                            title={isScheduledSaturday
                              ? "Rimuovi questo sabato lavorativo"
                              : "Imposta o sposta qui il sabato lavorativo del mese"}
                            className={`h-6 w-6 rounded inline-flex items-center justify-center ${
                              isScheduledSaturday
                                ? "text-sky-700 bg-sky-100 hover:bg-sky-200"
                                : "text-suite-primary hover:bg-suite-primary-light"
                            }`}
                          >
                            <Calendar size={12} />
                          </button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right px-2 py-2 align-top">
                      <Input
                        type="text"
                        inputMode="decimal"
                        pattern={durFmt === "hm" ? undefined : "[0-9]*[.,]?[0-9]{0,2}"}
                        value={row.theoretical_override !== undefined ? String(row.theoretical_override).replace(".", ",") : ""}
                        onChange={(e) => setTheoretical(d.date, e.target.value)}
                        onBlur={() => commitTheoretical(d.date)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitTheoretical(d.date);
                            focusNextInColumn(d.date, "att-teor-{date}");
                          }
                        }}
                        aria-invalid={isDurationInvalid(row.theoretical_override, durFmt) || undefined}
                        className={`${isDurationInvalid(row.theoretical_override, durFmt) ? DUR_INVALID : ""} w-full h-7 text-right font-mono text-xs px-1.5 ${
                          isScheduledSaturday
                            ? "text-sky-700 placeholder:text-sky-700 border-sky-300 bg-sky-50 font-semibold"
                            : (row.theoretical_override !== undefined
                              ? "text-suite-neutral-800 border-suite-accent-border bg-suite-accent-light/40"
                              : "text-suite-neutral-400")
                        }`}
                        placeholder={fmtHours(d.theoretical)}
                        data-testid={`att-teor-${d.date}`}
                      />
                    </TableCell>
                    <TableCell className="px-3 py-2 align-top">
                      <CauseChipsEditor
                        date={d.date}
                        causeHours={row.cause_hours || {}}
                        onSet={setCause}
                        onCommit={commitCause}
                        onEnter={(date, c) => focusNextInColumn(date, `att-chip-input-{date}-${c}`)}
                      />
                    </TableCell>
                    <TableCell className={`text-right font-mono font-semibold whitespace-nowrap px-2 text-xs align-top pt-3 ${d.daily_balance >= 0 ? "text-suite-accent-hover" : "text-suite-error"}`}>
                      {fmtHoursSigned(d.daily_balance)}
                    </TableCell>
                    <TableCell className="px-2 py-2 align-top">
                      <Textarea
                        value={row.notes || ""}
                        onChange={(e) => setNotes(d.date, e.target.value)}
                        rows={1}
                        className="min-h-[28px] max-h-24 text-xs py-1 px-2 w-full resize-y leading-tight"
                        placeholder="—"
                        data-testid={`att-notes-${d.date}`}
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap px-2 align-top pt-3">
                      <StatusBadge s={row.status || d.status} />
                    </TableCell>
                  </TableRow>
                );
              })}
              {(!monthData || filteredDays.length === 0) && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-suite-neutral-400 py-8">
                    <ClipboardList size={28} className="mx-auto mb-2 opacity-50" />
                    {monthLoading
                      ? "Caricamento del mese selezionato…"
                      : "Nessun dato — seleziona dipendente e mese"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
        </TooltipProvider>
      </SuiteCard>
      </fieldset>

      {/* v1.5.4 · ActionCatalog rimosso dalla toolbar: le azioni sono ora nel menu "Altre operazioni". */}
    </div>
    {/* FND-13 · Workspace Smart Side Panel (Sprint V3.1) — rail destro riutilizzabile.
        Contenuto placeholder AI/Anomalie/Timeline/Note conforme al mockup V2 approvato.
        `hidden xl:flex` → non presente sotto 1280px (comportamento intenzionale). */}
    <div className="hidden xl:block xl:sticky xl:top-0 xl:self-start xl:h-screen">
      <WorkspaceSidePanel
        testid="attendance-side-panel"
        defaultTab="ai"
        tabs={[
          {
            key: "ai",
            label: "AI",
            aiPending: true,
            content: (
              <>
                <AIAssistantPlaceholder
                  testid="attendance-ai-placeholder"
                  moduleHint={
                    empDisplay && empDisplay !== "—"
                      ? `Analisi predittiva del cartellino di ${empDisplay}. Rilevamento anomalie, suggerimenti compilazione e classificazione automatica.`
                      : "Seleziona un dipendente per abilitare l'analisi AI del cartellino."
                  }
                />
                <div className="rounded-xl border border-suite-neutral-200 bg-white p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div
                      className="text-[10.5px] font-bold tracking-widest uppercase text-suite-neutral-500"
                      style={{ fontFamily: "Manrope" }}
                    >
                      Suggerimenti intelligenti
                    </div>
                    <span
                      className="text-[8.5px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded bg-purple-50 text-purple-600"
                      style={{ fontFamily: "Manrope" }}
                    >
                      Presto
                    </span>
                  </div>
                  <ul className="space-y-1.5 text-[11.5px] text-suite-neutral-600 opacity-60">
                    <li className="flex gap-1.5"><span className="text-suite-primary">▸</span>Compilare giornate isolate con orario teorico</li>
                    <li className="flex gap-1.5"><span className="text-suite-primary">▸</span>Autorizzare straordinari già presenti in commessa</li>
                    <li className="flex gap-1.5"><span className="text-suite-primary">▸</span>Verificare continuità certificati INPS</li>
                  </ul>
                </div>
              </>
            ),
          },
          {
            key: "timeline",
            label: "Timeline",
            content: (
              <div className="rounded-xl border border-suite-neutral-200 bg-white p-3">
                <div
                  className="text-[10.5px] font-bold tracking-widest uppercase text-suite-neutral-500 mb-2"
                  style={{ fontFamily: "Manrope" }}
                >
                  Storico modifiche del mese
                </div>
                <ol className="space-y-2 text-[11.5px] text-suite-neutral-600">
                  <li className="flex gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-suite-neutral-300 mt-1.5 shrink-0" />
                    <div>La timeline verrà popolata con gli eventi di audit del cartellino (import, salvataggi, invii, approvazioni). Requisito legale di tracciabilità.</div>
                  </li>
                </ol>
              </div>
            ),
          },
          {
            key: "notes",
            label: "Note",
            content: (
              <div className="rounded-xl border border-suite-neutral-200 bg-white p-3">
                <div
                  className="text-[10.5px] font-bold tracking-widest uppercase text-suite-neutral-500 mb-2"
                  style={{ fontFamily: "Manrope" }}
                >
                  Nota del mese
                </div>
                <div className="text-[11.5px] text-suite-neutral-500 italic">
                  Testo libero unico per il cartellino del mese (sostituisce le note per riga). Predisposto per la Sprint V3.2.
                </div>
              </div>
            ),
          },
        ]}
      />
    </div>
    </div>
  );
}
